import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, NavLink, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Button,
  Empty,
  Input,
  List,
  Modal,
  Popconfirm,
  Select,
  Switch,
  Tag,
  Tooltip,
  Typography,
  Upload,
  message,
} from 'antd';
import {
  AppstoreOutlined,
  CloseOutlined,
  DeleteOutlined,
  DownOutlined,
  DownloadOutlined,
  ExportOutlined,
  FileImageOutlined,
  FileTextOutlined,
  InboxOutlined,
  LockOutlined,
  PlusOutlined,
  ReadOutlined,
  RightOutlined,
  SettingOutlined,
  TeamOutlined,
  ThunderboltOutlined,
  VideoCameraOutlined,
  PlayCircleOutlined,
} from '@ant-design/icons';
import AnimationStudio from '../illustration/AnimationStudio';
import {
  DOG_TRICKS,
  FLIGHT_POSE,
  FLIGHT_TOP_DOWN_POSE,
  LIFTOFF_POSE,
  HEAD_POSE,
  PERSON_ACTIVITY_POSES,
  ROAD_POSE,
  ROAD_TOP_DOWN_POSE,
  STANDING_POSE,
  TOP_DOWN_POSE,
  vanShowsFlight,
} from '../illustration/characterPoses';
import ClipLibrary from '../illustration/ClipLibrary';
import { GenerationThumb, ImageGallery, LazyImage, generationImageSrc, isVideoGeneration } from '../illustration/GenerationGallery';
import { bodyFor } from '../illustration/animationCatalog';
import { resolveClientCatalog } from '../illustration/expressionCatalog';
import { accordionKey, useAccordionOpen } from '../illustration/accordionState';
import {
  addChapter,
  addCharacter,
  addOther,
  addPage,
  archiveChapter,
  archiveCharacter,
  archiveOther,
  archivePage,
  archiveProject,
  deleteChapter,
  deleteCharacter,
  deleteDocument,
  deleteOther,
  deletePage,
  generateTreatmentSheet,
  approveGeneration,
  deleteGeneration,
  unlockGeneration,
  exportGeneration,
  downloadExportFile,
  generateScene,
  updateExpressionCatalog,
  getDocument,
  getProject,
  shouldMirrorProjectToCloud,
  ingestBible,
  exportBibleChanges,
  listDocumentSections,
  listDocuments,
  listGenerations,
  restoreChapter,
  restoreCharacter,
  restoreOther,
  restorePage,
  seedChapterFromDocument,
  updateChapter,
  updateCharacter,
  uploadCharacterBaseReference,
  clearCharacterBaseReference,
  uploadOtherBaseReference,
  clearOtherBaseReference,
  updateOther,
  updatePage,
  updateProject,
  uploadDocument,
} from '../illustration/api';
import { EXPORT_DPI, WORK_DPI } from '../illustration/constants';
import { writesStayLocal } from '../illustration/localOnly';
import { useStoredPicture } from '../illustration/useStoredPicture';
import { attachGenerationPicture, syncLocalApprovedReference } from '../illustration/cloudGenerations';
import { usePictureUrl } from '../illustration/pictureLoad';
import WatercolorLoader from '../components/WatercolorLoader';
import './ProjectPage.css';

function isArchivedItem(item) {
  if (!item) return false;
  if (item.archived === true) return true;
  return item.status === 'archived';
}

function BibleMatchChip({ status, version }) {
  if (!version || !status) return null;
  const label =
    status === 'match'
      ? `Bible v${version}`
      : status === 'differs'
        ? `Differs from v${version}`
        : 'Not in this bible';
  return <span className={`bible-match bible-match--${status}`}>{label}</span>;
}

function NewPromptChip({ page, bibleVersion }) {
  const mark = page?.promptRevisedIn;
  if (!mark) return null;
  if (bibleVersion && String(mark) !== String(bibleVersion)) return null;
  const label = /^\d/.test(String(mark)) ? `New prompt · v${mark}` : 'New prompt';
  return <span className="bible-match bible-match--new">{label}</span>;
}

function latestGenerateError(generations, match) {
  const newest = (generations || [])
    .filter(match)
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0];
  return newest?.error ? String(newest.error) : '';
}

function displayGenerateError(localError, savedError) {
  if (savedError && (!localError || /failed to fetch/i.test(localError))) return savedError;
  return localError || savedError || '';
}

async function resolveGenerateError(projectId, error) {
  const direct = String(error?.message || error || '').trim();
  if (direct && !/failed to fetch/i.test(direct)) return direct;
  const saved = await listGenerations(projectId)
    .then((data) => latestGenerateError(data.generations, () => true))
    .catch(() => '');
  return saved || direct || 'Generate failed';
}

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

/** Tabs: Characters | Other | Chapters | Documents | Settings. organizationModel ignored for labels. */
function sectionLinksForProject(_project) {
  return [
    { to: 'characters', label: 'Characters', icon: <TeamOutlined /> },
    { to: 'other', label: 'Other', icon: <AppstoreOutlined /> },
    { to: 'chapters', label: 'Chapters', icon: <ReadOutlined /> },
    { to: 'treatments', label: 'Treatment', icon: <FileImageOutlined /> },
    { to: 'documents', label: 'Documents', icon: <FileTextOutlined /> },
    { to: 'animation', label: 'Animation (beta)', icon: <VideoCameraOutlined /> },
    { to: 'library', label: 'Library', icon: <PlayCircleOutlined /> },
    { to: 'settings', label: 'Settings', icon: <SettingOutlined />, iconOnly: true },
  ];
}

const CHARACTER_KINDS = new Set(['person', 'dog', 'van', 'tree']);

const PERSON_FACE_POSES = [
  { id: 'smiling', label: 'smiling' },
  { id: 'smirking', label: 'smirking' },
  { id: 'laughing', label: 'laughing' },
  { id: 'frowning', label: 'frowning' },
  { id: 'surprised', label: 'surprised' },
  { id: 'mad', label: 'mad' },
];

const PERSON_SHEET_POSES = [{ id: 'turnaround', label: 'turnaround' }];

const DEFAULT_POSES = {
  dog: [
    STANDING_POSE,
    HEAD_POSE,
    { id: 'full-body', label: 'full body' },
    { id: 'sitting', label: 'sitting' },
    { id: 'chasing-squirrel', label: 'chasing squirrel' },
    { id: 'jumping-up', label: 'jumping up' },
    { id: 'sleeping-by-fire', label: 'sleeping by the fire' },
    ...DOG_TRICKS,
  ],
  person: [
    STANDING_POSE,
    HEAD_POSE,
    { id: 'full-body', label: 'full body' },
    ...PERSON_FACE_POSES,
    ...PERSON_ACTIVITY_POSES,
  ],
  van: [
    STANDING_POSE,
    TOP_DOWN_POSE,
    ROAD_POSE,
    ROAD_TOP_DOWN_POSE,
    { id: 'three-view', label: 'front · ¾ · side' },
    { id: 'parked-right', label: 'parked right' },
    { id: 'pop-top-open', label: 'pop-top open' },
    { id: 'gear-on-roof', label: 'gear on roof' },
  ],
  tree: [
    { id: 'full-body', label: 'full body' },
    { id: 'sapling', label: 'sapling' },
    { id: 'hammock', label: 'hammock' },
    { id: 'rope-swing', label: 'rope swing' },
    { id: 'lightning-struck', label: 'lightning-struck' },
    { id: 'tree-b-beside-stump', label: 'Tree B beside stump' },
  ],
};

function chapterJumpLabel(chapter) {
  if (chapter?.year != null && chapter.year !== '') return String(chapter.year);
  return chapter?.title || 'Chapter';
}

function castStillInBook(cast, characters) {
  const byId = new Map((characters || []).map((character) => [character.id, character]));
  return (cast || []).filter((member) => {
    if (!member?.characterId) return true;
    const character = byId.get(member.characterId);
    return Boolean(character) && !isArchivedItem(character);
  });
}

function charactersClaimedByChapter(characters, chapter) {
  const castIds = new Set((chapter?.cast || []).map((member) => member.characterId).filter(Boolean));
  const year = chapter?.year;
  return (characters || []).filter((character) => {
    if (castIds.has(character.id)) return true;
    return year != null && year !== '' && character.meta?.year === year;
  });
}

function yearsAreSameChapter(left, right) {
  const a = Number(left);
  const b = Number(right);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return false;
  if (a === b) return true;
  return (a === 2065 && b === 2068) || (a === 2068 && b === 2065);
}

function spanFromPlacement(placement) {
  const text = String(placement || '');
  const range = text.match(/\bp\s*(\d+)\s*[–—-]\s*(\d+)\b/i);
  if (range) return { start: Number(range[1]), end: Number(range[2]) };
  const one = text.match(/\bp\s*(\d+)\b/i);
  if (one) return { start: Number(one[1]), end: Number(one[1]) };
  return null;
}

function placementOverlapsPage(placement, pageStart, pageEnd) {
  const text = String(placement || '');
  const spans = [];
  const rangeRe = /\bp?\s*(\d+)\s*[–—-]\s*(\d+)\b/g;
  let match;
  while ((match = rangeRe.exec(text))) {
    spans.push([Number(match[1]), Number(match[2])]);
  }
  if (!spans.length) {
    const oneRe = /\bp\s*(\d+)\b/g;
    while ((match = oneRe.exec(text))) spans.push([Number(match[1]), Number(match[1])]);
  }
  if (!spans.length) return false;
  return spans.some(([start, end]) => start <= pageEnd && end >= pageStart);
}

function idList(value) {
  return Array.isArray(value) ? value.filter(Boolean) : [];
}

function othersForPage(others, page, chapter) {
  const year = page?.year ?? chapter?.year ?? null;
  const pageStart = page?.pageStart;
  const pageEnd = page?.pageEnd ?? pageStart;
  const seek = (others || []).filter((item) => !isArchivedItem(item) && isSeekAndFindItem(item));
  const inYear = (item) =>
    year == null || item.meta?.year == null || yearsAreSameChapter(item.meta.year, year);
  const automatic = /seek-and-find/i.test(String(page?.title || ''))
    ? seek.filter(inYear)
    : seek.filter((item) => {
        if (!inYear(item)) return false;
        if (pageStart == null) return false;
        const fromPlacement = spanFromPlacement(item.meta?.placement);
        const start = fromPlacement?.start ?? item.meta?.pageStart;
        const end = fromPlacement?.end ?? item.meta?.pageEnd ?? start;
        if (start != null) return start <= pageEnd && end >= pageStart;
        return placementOverlapsPage(item.meta?.placement, pageStart, pageEnd);
      });
  const exclude = new Set(idList(page?.otherExcludeIds));
  const byId = new Map(seek.map((item) => [item.id, item]));
  const chosen = new Map();
  for (const item of automatic) {
    if (item?.id && !exclude.has(item.id)) chosen.set(item.id, item);
  }
  for (const id of idList(page?.otherIncludeIds)) {
    if (exclude.has(id) || chosen.has(id)) continue;
    const item = byId.get(id);
    if (item) chosen.set(id, item);
  }
  return [...chosen.values()];
}

function pageOtherPatch(page, itemId, action) {
  const include = new Set(idList(page?.otherIncludeIds));
  const exclude = new Set(idList(page?.otherExcludeIds));
  if (action === 'remove') {
    include.delete(itemId);
    exclude.add(itemId);
  } else {
    exclude.delete(itemId);
    include.add(itemId);
  }
  return { otherIncludeIds: [...include], otherExcludeIds: [...exclude] };
}

function lockedOtherPicture(item, generations) {
  return (generations || [])
    .filter(
      (generation) =>
        generation.otherId === item.id &&
        !generation.error &&
        hasPicture(generation) &&
        (generation.lockedAsReference || generation.approved)
    )
    .sort((a, b) => String(b.createdAt || '').localeCompare(String(a.createdAt || '')))[0] || null;
}

function groupOthersByChapter(items, chapters) {
  const remaining = new Set((items || []).map((item) => item.id));
  const groups = [];
  (chapters || []).forEach((chapter) => {
    const members = (items || []).filter(
      (item) => remaining.has(item.id) && yearsAreSameChapter(item.meta?.year, chapter.year)
    );
    members.forEach((item) => remaining.delete(item.id));
    if (members.length) groups.push({ chapter, items: members });
  });
  const rest = (items || []).filter((item) => remaining.has(item.id));
  return { groups, rest };
}

function groupCharactersByChapter(characters, chapters) {
  const remaining = new Set((characters || []).map((character) => character.id));
  const groups = [];
  (chapters || []).forEach((chapter) => {
    const members = charactersClaimedByChapter(characters, chapter).filter((character) =>
      remaining.has(character.id)
    );
    members.forEach((character) => remaining.delete(character.id));
    if (members.length) groups.push({ chapter, characters: members });
  });
  const rest = (characters || []).filter((character) => remaining.has(character.id));
  return { groups, rest };
}

function ChapterJump({ chapters, idPrefix }) {
  if (!chapters || chapters.length < 2) return null;
  return (
    <nav className="chapter-jump" aria-label="Jump to chapter">
      {chapters.map((chapter) => {
        const label = chapterJumpLabel(chapter);
        return (
          <a key={chapter.id} className="chapter-jump__link" href={`#${idPrefix}${chapter.id}`}>
            {label}
          </a>
        );
      })}
    </nav>
  );
}

function characterKind(character) {
  const raw = String(character?.meta?.kind || '').toLowerCase();
  if (CHARACTER_KINDS.has(raw)) return raw;
  const role = String(character?.meta?.role || character?.title || '').toLowerCase();
  if (/\b(dog|mutt|dane|puppy|breed)\b/.test(role)) return 'dog';
  if (/\b(van|vehicle|westfalia|vw)\b/.test(role)) return 'van';
  if (/\btree\b/.test(role)) return 'tree';
  return 'person';
}

function posesWithFlight(poses, item, kind) {
  if (kind !== 'van' || item?.meta?.viewPrompts) return poses;
  const extra = [];
  if (String(item?.meta?.animation?.liftoff || '').trim()) extra.push(LIFTOFF_POSE);
  if (vanShowsFlight(item)) extra.push(FLIGHT_TOP_DOWN_POSE, FLIGHT_POSE);
  return [
    ...poses,
    ...extra.filter(
      (pose) => !poses.some((existing) => String(existing.label || '').toLowerCase() === pose.label)
    ),
  ];
}

function posesForCharacter(character) {
  const fromMeta = character?.meta?.poses;
  if (character?.meta?.viewPrompts && Array.isArray(fromMeta) && fromMeta.length) {
    return fromMeta.map((p, i) =>
      typeof p === 'string'
        ? { id: p, label: p }
        : { id: p.id || p.label || `pose-${i}`, label: p.label || p.id || String(p) }
    );
  }
  const defaults = DEFAULT_POSES[characterKind(character)] || DEFAULT_POSES.person;
  if (!Array.isArray(fromMeta) || !fromMeta.length) return defaults;
  const custom = fromMeta.map((p, i) =>
    typeof p === 'string'
      ? { id: p, label: p }
      : { id: p.id || p.label || `pose-${i}`, label: p.label || p.id || String(p) }
  );
  const seen = new Set(defaults.map((pose) => pose.label.toLowerCase()));
  return [
    ...defaults,
    ...custom.filter((pose) => pose.label && !seen.has(pose.label.toLowerCase())),
  ];
}

function isSeekAndFindItem(item) {
  const kind = String(item?.meta?.kind || '').toLowerCase();
  if (kind === 'seek-and-find' || kind === 'seekandfind' || kind === 'secret') return true;
  const hay = `${item?.title || ''} ${item?.summary || ''} ${item?.meta?.section || ''}`.toLowerCase();
  return /seek[\s-]?and[\s-]?find|secret/.test(hay);
}

function hasPicture(generation) {
  if (!generation || generation.error) return false;
  return Boolean(
    generation.output?.imageUrl ||
      generation.output?.path ||
      generation.output?.contentUrl ||
      generation.storagePath
  );
}

function keepPicture(previous, next) {
  if (!next) return next;
  const imageUrl = next.output?.imageUrl || previous?.output?.imageUrl;
  if (!imageUrl) return next;
  return { ...next, output: { ...(next.output || {}), imageUrl } };
}

function BaseReferenceImage({ contentUrl, alt, className = 'base-ref__preview' }) {
  const src = useStoredPicture(contentUrl);
  if (!src) return null;
  return <LazyImage className={className} src={src} alt={alt} />;
}

function LockedOtherThumb({ projectId, generation }) {
  const loaded = usePictureUrl(projectId, generation);
  const src = generationImageSrc(projectId, generation) || (loaded && loaded !== 'failed' ? loaded : '');
  if (!src) {
    return (
      <span className="page-other-refs__missing">
        {loaded === 'failed' ? "Picture didn't load." : ''}
      </span>
    );
  }
  return <LazyImage className="page-other-refs__thumb" src={src} alt="" />;
}

function PageOtherRefs({ projectId, items, addable, generations, disabled, onRemove, onAdd }) {
  const [addKey, setAddKey] = useState(0);
  if (!items?.length && !addable?.length) return null;
  return (
    <div className="page-other-refs">
      <p className="page-other-refs__label">Other on this page</p>
      {items?.length ? (
        <ul className="page-other-refs__list">
          {items.map((item) => {
            const locked = lockedOtherPicture(item, generations);
            return (
              <li key={item.id} className="page-other-refs__item">
                {locked ? (
                  <LockedOtherThumb projectId={projectId} generation={locked} />
                ) : item.meta?.baseReferenceContentUrl ? (
                  <BaseReferenceImage
                    className="page-other-refs__thumb"
                    contentUrl={item.meta.baseReferenceContentUrl}
                    alt=""
                  />
                ) : (
                  <span className="page-other-refs__missing">No locked picture</span>
                )}
                <button
                  type="button"
                  className="page-other-refs__remove"
                  aria-label={`Remove ${item.title}`}
                  disabled={disabled}
                  onClick={() => onRemove?.(item.id)}
                >
                  <CloseOutlined aria-hidden />
                </button>
                <span className="page-other-refs__name">{item.title}</span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="page-other-refs__empty">None on this page.</p>
      )}
      {addable?.length ? (
        <Select
          key={addKey}
          className="page-other-refs__add"
          placeholder="Add an item"
          aria-label="Add an item"
          disabled={disabled}
          showSearch
          optionFilterProp="label"
          options={addable.map((item) => ({ value: item.id, label: item.title }))}
          onChange={(id) => {
            setAddKey((key) => key + 1);
            onAdd?.(id);
          }}
        />
      ) : null}
    </div>
  );
}

/** Compact delete/remove confirm — small popover above the trigger (not a modal). */
function DeleteConfirmButton({
  onConfirm,
  label = 'Delete',
  /** 'remove' → Yes, Remove · 'delete' → Yes, Delete */
  action,
  title = 'Are you sure?',
  loading = false,
  size = 'middle',
  type,
  className,
  ariaLabel,
  children,
  iconOnly = false,
  placement = 'top',
}) {
  const mode = action || (/^remove$/i.test(String(label)) ? 'remove' : 'delete');
  const okText = mode === 'remove' ? 'Yes, Remove' : 'Yes, Delete';
  return (
    <Popconfirm
      title={title}
      okText={okText}
      cancelText="No, cancel"
      okButtonProps={{ danger: true, size: 'small' }}
      cancelButtonProps={{ size: 'small' }}
      placement={placement}
      rootClassName="confirm-popconfirm"
      onConfirm={onConfirm}
    >
      <Button
        danger
        size={size}
        type={type}
        className={['era-control', iconOnly ? 'era-control--icon' : null, className].filter(Boolean).join(' ')}
        icon={<DeleteOutlined />}
        loading={loading}
        aria-label={ariaLabel || label}
      >
        {iconOnly ? null : (children ?? label)}
      </Button>
    </Popconfirm>
  );
}


function PrintExportButton({ generation, exportingId, onExportGeneration }) {
  const ready = Boolean(generation?.export300?.path);
  return (
    <Tooltip
      title={
        ready
          ? 'Download the 300dpi file. This does not make a new picture.'
          : 'Upsize to 300dpi'
      }
    >
      <Button
        htmlType="button"
        type="text"
        size="small"
        className="era-control era-scene-gallery__export"
        icon={ready ? <DownloadOutlined /> : <PlusOutlined />}
        loading={exportingId === generation?.id}
        aria-label={ready ? 'Download 300dpi' : 'Upsize to 300dpi'}
        onClick={() => onExportGeneration(generation, ready)}
      />
    </Tooltip>
  );
}

/** Approve / Export / Delete controls shared by attempt galleries. */
function GenerationAttemptActions({
  generation,
  approvingId,
  unlockingId,
  deletingId,
  exportingId,
  onApprove,
  onUnlock,
  onDelete,
  onExportGeneration,
  onReuseOverride,
  showExport = false,
}) {
  const g = generation;
  if (!g) return null;
  const locked = !!(g.lockedAsReference || g.approved);
  return (
    <div className="era-scene-gallery__actions">
      {locked ? (
        <div className="era-scene-gallery__locked">
          <Popconfirm
            title="Are you sure?"
            okText="Yes, unlock"
            cancelText="No, cancel"
            okButtonProps={{ size: 'small' }}
            cancelButtonProps={{ size: 'small' }}
            placement="top"
            rootClassName="confirm-popconfirm"
            onConfirm={() => onUnlock?.(g)}
            disabled={!onUnlock}
          >
            <Button
              htmlType="button"
              type="text"
              size="small"
              className="era-control era-scene-gallery__lock"
              icon={<LockOutlined />}
              loading={unlockingId === g.id}
              disabled={!onUnlock}
              aria-label="Unlock this picture"
            />
          </Popconfirm>
          <Tag className="era-scene-gallery__lock-chip">Locked</Tag>
          {onExportGeneration ? (
            <PrintExportButton generation={g} exportingId={exportingId} onExportGeneration={onExportGeneration} />
          ) : null}
        </div>
      ) : (
        <Button
          htmlType="button"
          type="default"
          size="small"
          className="era-control era-scene-gallery__approve"
          loading={approvingId === g.id}
          disabled={!onApprove}
          onClick={() => onApprove?.(g)}
        >
          Lock
        </Button>
      )}
      {typeof onReuseOverride === 'function' && (g.override || g.castOverridePrefixes?.length) ? (
        <Button type="link" size="small" onClick={() => onReuseOverride(g)}>
          Reuse override
        </Button>
      ) : null}
      {!locked && onDelete ? (
        <DeleteConfirmButton
          label="Delete"
          size="small"
          type="text"
          loading={deletingId === g.id}
          ariaLabel={`Delete generation ${g.id}`}
          onConfirm={() => onDelete(g)}
        />
      ) : null}
    </div>
  );
}

function ProjectSectionNav({ projectId, project }) {
  const links = sectionLinksForProject(project);
  return (
    <div className="project-nav">
      <nav className="project-tabs" aria-label="Project sections">
        {links.map((item) => (
          <NavLink
            key={item.to}
            to={`/projects/${projectId}/${item.to}`}
            className={({ isActive }) =>
              [
                'project-tab',
                item.iconOnly ? 'project-tab--icon' : '',
                isActive ? 'project-tab--active' : '',
              ]
                .filter(Boolean)
                .join(' ')
            }
            aria-label={item.iconOnly ? item.label : undefined}
            title={item.label}
          >
            <span aria-hidden="true">{item.icon}</span>
            {!item.iconOnly && <span className="project-tab__label">{item.label}</span>}
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

/**
 * Shared lifecycle for Treatments / Characters / Other:
 * edit in place (title + markdown summary) → archive/restore → delete with confirm.
 */
function StructureItemRow({ item, noun, onUpdate, onArchive, onRestore, onDelete }) {
  const [titleValue, setTitleValue] = useState(item.title || '');
  const [summaryValue, setSummaryValue] = useState(item.summary || '');
  const [saving, setSaving] = useState(false);
  const archived = isArchivedItem(item);

  useEffect(() => {
    setTitleValue(item.title || '');
    setSummaryValue(item.summary || '');
  }, [item.id, item.title, item.summary]);

  const save = async () => {
    if (!titleValue.trim()) {
      message.warning('Title is required');
      return;
    }
    setSaving(true);
    try {
      await onUpdate({ title: titleValue.trim(), summary: summaryValue });
      message.success('Saved');
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await onDelete();
      message.success(`${noun[0].toUpperCase()}${noun.slice(1)} deleted`);
    } catch (e) {
      message.error(e.message);
      throw e;
    }
  };

  return (
    <section
      style={{
        padding: 'var(--space-4)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-md)',
        background: archived ? 'var(--color-bg-sunken)' : 'var(--color-bg-surface)',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        opacity: archived ? 0.85 : 1,
      }}
    >
      <div className="flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        {archived ? <Tag>Archived</Tag> : <span />}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {archived ? (
            <Button
              size="small"
              icon={<InboxOutlined />}
              onClick={async () => {
                try {
                  await onRestore();
                  message.success('Restored');
                } catch (e) {
                  message.error(e.message);
                }
              }}
            >
              Restore
            </Button>
          ) : (
            <Button
              size="small"
              icon={<InboxOutlined />}
              onClick={async () => {
                try {
                  await onArchive();
                  message.success('Archived');
                } catch (e) {
                  message.error(e.message);
                }
              }}
            >
              Archive
            </Button>
          )}
          <DeleteConfirmButton
            label="Delete"
            ariaLabel={`Delete ${noun}`}
            onConfirm={handleDelete}
          />
        </div>
      </div>
      <Input
        value={titleValue}
        onChange={(e) => setTitleValue(e.target.value)}
        placeholder="Title"
        disabled={archived}
      />
      <Input.TextArea
        rows={3}
        value={summaryValue}
        onChange={(e) => setSummaryValue(e.target.value)}
        placeholder="Notes / summary (markdown ok)"
        disabled={archived}
        style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-body-sm)' }}
      />
      {!archived && (
        <div>
          <Button type="primary" size="small" loading={saving} onClick={save}>
            Save
          </Button>
        </div>
      )}
    </section>
  );
}

/** @deprecated Prefer CharactersSection / OtherSection — kept for reference. */
function StructureSection({
  title,
  empty,
  items,
  addLabel,
  noun,
  onAdd,
  onUpdate,
  onArchive,
  onRestore,
  onDelete,
}) {
  const [adding, setAdding] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [summaryValue, setSummaryValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const active = (items || []).filter((item) => !isArchivedItem(item));
  const archived = (items || []).filter((item) => isArchivedItem(item));
  const visible = showArchived ? archived : active;

  const submit = async () => {
    if (!titleValue.trim()) {
      message.warning('Title is required');
      return;
    }
    setSaving(true);
    try {
      await onAdd({ title: titleValue.trim(), summary: summaryValue });
      setTitleValue('');
      setSummaryValue('');
      setAdding(false);
      message.success('Added');
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div className="flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <Text type="secondary">{title}</Text>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {archived.length > 0 && (
            <Button type="link" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? 'Show active' : `Archived (${archived.length})`}
            </Button>
          )}
          {!showArchived && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
              {addLabel}
            </Button>
          )}
        </div>
      </div>

      {adding && !showArchived && (
        <div
          style={{
            padding: 'var(--space-4)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxWidth: 520,
          }}
        >
          <Input
            placeholder="Title"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            autoFocus
          />
          <Input.TextArea
            rows={3}
            placeholder="Notes / summary (markdown ok)"
            value={summaryValue}
            onChange={(e) => setSummaryValue(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="primary" loading={saving} onClick={submit}>
              Create
            </Button>
            <Button
              onClick={() => {
                setAdding(false);
                setTitleValue('');
                setSummaryValue('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {!visible.length ? (
        <Empty
          description={
            showArchived ? `No archived ${noun}s.` : empty
          }
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
          {visible.map((item) => (
            <StructureItemRow
              key={item.id}
              item={item}
              noun={noun}
              onUpdate={(body) => onUpdate(item.id, body)}
              onArchive={() => onArchive(item.id)}
              onRestore={() => onRestore(item.id)}
              onDelete={() => onDelete(item.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function PoseChipGroup({ label, poses, selected, onSelect }) {
  if (!poses?.length) return null;
  return (
    <div className="pose-group">
      {label ? (
        <Text type="secondary" className="pose-group__label">
          {label}
        </Text>
      ) : null}
      <div className="pose-chips" role="group" aria-label={label || 'Pose chips'}>
        {poses.map((item) => (
          <Button
            key={item.id}
            size="small"
            type={selected === item.label ? 'primary' : 'default'}
            className="pose-chip"
            aria-pressed={selected === item.label}
            onClick={() => onSelect(item.label)}
          >
            {item.label}
          </Button>
        ))}
      </div>
    </div>
  );
}

function CharacterCard({
  item,
  projectId,
  generations,
  expressionCatalog,
  onStructureChange,
  onUpdate,
  onArchive,
  onRestore,
  onDelete,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
  onGenerated,
  onUpdated,
  onUploadBaseReference,
  onClearBaseReference,
  matchStatus,
  matchVersion,
}) {
  const kind = characterKind(item);
  const poses = posesForCharacter(item);
  const archived = isArchivedItem(item);
  const [titleValue, setTitleValue] = useState(item.title || '');
  const [basePrompt, setBasePrompt] = useState(
    item.meta?.basePrompt || item.summary || ''
  );
  const [pose, setPose] = useState(poses[0]?.label || '');
  const [expressionId, setExpressionId] = useState(null);
  const [rowEmotion, setRowEmotion] = useState(null);
  const [overrideFragment, setOverrideFragment] = useState('');
  const [mannerism, setMannerism] = useState('');
  const [override, setOverride] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [refStatus, setRefStatus] = useState('');
  const [localGeneration, setLocalGeneration] = useState(null);
  const [open, setOpen] = useAccordionOpen(accordionKey('character', projectId, item.id));
  const panelId = `character-panel-${item.id}`;

  useEffect(() => {
    setTitleValue(item.title || '');
    setBasePrompt(item.meta?.basePrompt || item.summary || '');
    const nextPoses = posesForCharacter(item);
    setPose((prev) => {
      if (nextPoses.some((p) => p.label === prev)) return prev;
      if (item?.meta?.viewPrompts && ['driving', 'liftoff', 'flight'].includes(prev)) return prev;
      if (vanShowsFlight(item) && (prev === FLIGHT_POSE.label || prev === FLIGHT_TOP_DOWN_POSE.label || prev === LIFTOFF_POSE.label)) return prev;
      return nextPoses[0]?.label || '';
    });
    // item fields listed explicitly — full `item` would retrigger on unrelated meta churn
    // eslint-disable-next-line react-hooks/exhaustive-deps -- pose chips keyed off stable fields
  }, [item.id, item.title, item.summary, item.meta?.basePrompt, item.meta?.poses, item.meta?.kind]);

  const expressions = useMemo(
    () => resolveClientCatalog(expressionCatalog, kind, item.id),
    [expressionCatalog, kind, item.id]
  );
  const expressionEmotions = useMemo(
    () => [...new Set(expressions.map((entry) => entry.emotion))],
    [expressions]
  );

  useEffect(() => {
    const entry = expressions.find((candidate) => candidate.id === expressionId);
    setOverrideFragment(entry ? entry.fragment : '');
    const saved = expressionCatalog?.characters?.[item.id]?.[expressionId]?.body?.fragment;
    setMannerism(saved ? String(saved) : '');
  }, [expressionId, expressions, expressionCatalog, item.id]);

  const activityPoses = [
    ...PERSON_SHEET_POSES,
    ...PERSON_ACTIVITY_POSES,
    ...poses.filter((extra) => {
      const label = String(extra.label || '').toLowerCase();
      if (!label || label === 'full body' || label === STANDING_POSE.label || label === HEAD_POSE.label) return false;
      if (PERSON_SHEET_POSES.some((pose) => pose.label.toLowerCase() === label)) return false;
      if (PERSON_FACE_POSES.some((pose) => pose.label.toLowerCase() === label)) return false;
      if (PERSON_ACTIVITY_POSES.some((pose) => pose.label.toLowerCase() === label)) return false;
      return true;
    }),
  ];
  const activityLabels = new Set(activityPoses.map((item) => item.label));
  const isActivityPose = (label) =>
    activityLabels.has(label) && !PERSON_SHEET_POSES.some((item) => item.label === label);

  const selectPose = (label) => {
    const keepsExpression = label === HEAD_POSE.label || label === STANDING_POSE.label || isActivityPose(label);
    if (!keepsExpression) {
      setExpressionId(null);
      setRowEmotion(null);
    }
    setPose(label);
  };

  const selectActivity = (value) => {
    if (!value) {
      if (activityLabels.has(pose)) setPose(STANDING_POSE.label);
      return;
    }
    setPose(value);
  };

  const selectExpression = (emotion, value) => {
    if (!value) {
      if (rowEmotion === emotion) setRowEmotion(null);
      const current = expressions.find((entry) => entry.id === expressionId);
      if (current?.emotion === emotion) setExpressionId(null);
      return;
    }
    if (value === '__row__') {
      setExpressionId(null);
      setRowEmotion(emotion);
      return;
    }
    setRowEmotion(null);
    setExpressionId(value);
  };

  const matchesCharacter = (g) =>
    !g?.error &&
    g?.output &&
    hasPicture(g) &&
    g.target !== 'animation' &&
    !isVideoGeneration(g) &&
    g.characterId === item.id;

  const gallery = (generations || []).filter(matchesCharacter);
  if (localGeneration && matchesCharacter(localGeneration) && !gallery.some((g) => g.id === localGeneration.id)) {
    gallery.unshift(localGeneration);
  }
  const shownError = displayGenerateError(
    localError,
    latestGenerateError(generations, (g) => g.characterId === item.id)
  );

  const save = async () => {
    if (!titleValue.trim()) {
      message.warning('Title is required');
      return;
    }
    setSaving(true);
    try {
      await onUpdate({
        title: titleValue.trim(),
        summary: basePrompt,
        meta: {
          ...(item.meta || {}),
          kind,
          basePrompt,
          poses: posesForCharacter(item),
        },
      });
      message.success('Saved');
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runGenerate = async () => {
    if (busy || archived) return;
    setBusy(true);
    setLocalError(null);
    try {
      await onUpdate({
        title: titleValue.trim() || item.title,
        summary: basePrompt,
        meta: {
          ...(item.meta || {}),
          kind,
          basePrompt,
        },
      });
      const sheetChip = Boolean(item.meta?.viewPrompts?.[String(pose || '').toLowerCase()])
        || (pose === LIFTOFF_POSE.label && item.meta?.liftoffIllustration);
      const tallStill = !sheetChip && ([STANDING_POSE.label, TOP_DOWN_POSE.label, ROAD_POSE.label, ROAD_TOP_DOWN_POSE.label, 'driving', 'flight', LIFTOFF_POSE.label, FLIGHT_POSE.label, FLIGHT_TOP_DOWN_POSE.label].includes(pose)
        || isActivityPose(pose));
      const data = await generateScene(projectId, {
        characterId: item.id,
        pose: pose || undefined,
        override: override.trim() || undefined,
        target: 'character',
        providerId: 'openai',
        ...(tallStill ? { options: { size: '1024x1536' } } : {}),
      });
      const generation = data.generation;
      if (generation?.error) {
        setLocalError(generation.error);
        message.error(generation.error);
      } else {
        setLocalGeneration(generation);
        message.success(`Generated “${item.title}”`);
        onGenerated?.(generation);
      }
    } catch (e) {
      const msg = await resolveGenerateError(projectId, e);
      setLocalError(msg);
      message.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const persistCharacter = () => onUpdate({
    title: titleValue.trim() || item.title,
    summary: basePrompt,
    meta: {
      ...(item.meta || {}),
      kind,
      basePrompt,
    },
  });

  const runExpression = async () => {
    if (!expressionId) {
      message.info('Pick an expression first');
      return;
    }
    if (busy || archived) return;
    setBusy(true);
    setLocalError(null);
    try {
      await persistCharacter();
      if (expressionFieldsDirty()) await saveOverride({ quiet: true });
      const shot = pose === HEAD_POSE.label ? 'head' : 'body';
      const data = await generateScene(projectId, {
        characterId: item.id,
        target: 'expression',
        expressionId,
        shot,
        pose: shot === 'head' ? undefined : (isActivityPose(pose) ? pose : STANDING_POSE.label),
        providerId: 'openai',
        ...(shot === 'body' ? { options: { size: '1024x1536' } } : {}),
      });
      const generation = data.generation;
      if (generation?.error) {
        setLocalError(generation.error);
        message.error(generation.error);
      } else {
        setLocalGeneration(generation);
        message.success(`Generated “${item.title}”`);
        onGenerated?.(generation);
      }
    } catch (e) {
      const msg = await resolveGenerateError(projectId, e);
      setLocalError(msg);
      message.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const runExpressionRow = async (emotion) => {
    if (busy || archived) return;
    setBusy(true);
    setLocalError(null);
    try {
      await persistCharacter();
      const shot = pose === HEAD_POSE.label ? 'head' : 'body';
      const data = await generateScene(projectId, {
        characterId: item.id,
        target: 'expression-row',
        emotion,
        shot,
        pose: shot === 'head' ? undefined : (isActivityPose(pose) ? pose : STANDING_POSE.label),
        providerId: 'openai',
      });
      const generation = data.generation;
      if (generation?.error) {
        setLocalError(generation.error);
        message.error(generation.error);
      } else {
        setLocalGeneration(generation);
        message.success(`Generated ${emotion} row`);
        onGenerated?.(generation);
      }
    } catch (e) {
      const msg = await resolveGenerateError(projectId, e);
      setLocalError(msg);
      message.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const expressionCellPatch = () => {
    const cell = { fragment: overrideFragment };
    const gesture = mannerism.trim();
    if (gesture) cell.body = { fragment: gesture };
    return { characters: { [item.id]: { [expressionId]: cell } } };
  };

  const expressionFieldsDirty = () => {
    const entry = expressions.find((candidate) => candidate.id === expressionId);
    const savedFace = entry ? entry.fragment : '';
    const savedGesture = String(
      expressionCatalog?.characters?.[item.id]?.[expressionId]?.body?.fragment || ''
    ).trim();
    return overrideFragment !== savedFace || mannerism.trim() !== savedGesture;
  };

  const saveOverride = async ({ quiet = false } = {}) => {
    if (!expressionId) return;
    try {
      const data = await updateExpressionCatalog(projectId, expressionCellPatch());
      onStructureChange?.(data);
      if (!quiet) message.success('Saved on this character');
    } catch (e) {
      setLocalError(e.message);
      if (!quiet) message.error(e.message);
      else throw e;
    }
  };

  const activityPicker = kind === 'person' ? (
    <div className="activity-picker">
      <div className="expression-picker__field">
        <label className="expression-picker__label" htmlFor={`activity-${item.id}`}>
          Activity
        </label>
        <Select
          id={`activity-${item.id}`}
          allowClear
          disabled={busy}
          placeholder="Choose"
          value={activityLabels.has(pose) ? pose : undefined}
          onChange={selectActivity}
          options={activityPoses.map((activity) => ({ value: activity.label, label: activity.label }))}
          aria-label="Activity"
        />
      </div>
    </div>
  ) : null;

  const expressionPicker = (kind === 'person' || kind === 'dog') ? (
    <div className="expression-picker">
      {expressionEmotions.map((emotion) => {
        const cells = expressions.filter((entry) => entry.emotion === emotion);
        const selectedHere = cells.some((entry) => entry.id === expressionId);
        const fieldId = `expression-${item.id}-${emotion}`;
        return (
          <div key={emotion} className="expression-picker__field">
            <label className="expression-picker__label" htmlFor={fieldId}>
              {emotion}
            </label>
            <Select
              id={fieldId}
              allowClear
              disabled={busy}
              placeholder="Choose"
              value={rowEmotion === emotion ? '__row__' : selectedHere ? expressionId : undefined}
              onChange={(value) => selectExpression(emotion, value)}
              options={[
                { value: '__row__', label: 'Generate row' },
                ...cells.map((entry) => ({ value: entry.id, label: entry.label })),
              ]}
              aria-label={`${emotion} expression`}
            />
          </div>
        );
      })}
    </div>
  ) : null;

  const sharedMannerism = expressionId
    ? String(bodyFor(expressionId, kind === 'dog' ? 'dog' : 'person')?.fragment || '').trim()
    : '';

  return (
    <section className={`era-card${archived ? ' era-card--archived' : ''}`}>
      <div className="era-card__header">
        <div className="era-card__top">
        <div className="era-card__title-block">
          <Text type="secondary" className="era-card__meta">
            <button
              type="button"
              className="era-card__toggle"
              aria-expanded={open}
              aria-controls={panelId}
              onClick={() => setOpen((value) => !value)}
            >
              {open ? <DownOutlined /> : <RightOutlined />}
              <span>{open ? 'Hide' : 'Show'}</span>
            </button>
            <Tag>{kind}</Tag>
            {archived ? ' · Archived' : ''}
            <BibleMatchChip status={matchStatus} version={matchVersion} />
          </Text>
          <Input
            className="era-card__title-input"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            disabled={archived}
            aria-label="Character title"
          />
        </div>
        {open ? (
          <div className="era-card__corner">
            {!archived ? (
              <Tooltip title="Archive">
                <Button
                  htmlType="button"
                  className="era-control era-card__icon-btn"
                  icon={<InboxOutlined />}
                  aria-label={`Archive character ${item.title}`}
                  onClick={async () => {
                    await onArchive();
                    message.success('Archived');
                    onUpdated?.();
                  }}
                />
              </Tooltip>
            ) : null}
            <DeleteConfirmButton
              iconOnly
              placement="bottomRight"
              className="era-card__delete era-card__icon-btn"
              ariaLabel={`Delete character ${item.title}`}
              onConfirm={async () => {
                try {
                  await onDelete();
                  message.success('Character deleted');
                  onUpdated?.();
                } catch (e) {
                  message.error(e.message);
                  throw e;
                }
              }}
            />
          </div>
        ) : null}
        </div>
        {archived ? (
          <div className="era-card__actions">
            <div className="era-card__actions-end">
              <Button
                htmlType="button"
                className="era-control"
                onClick={async () => {
                  await onRestore();
                  message.success('Restored');
                  onUpdated?.();
                }}
              >
                Restore
              </Button>
            </div>
          </div>
        ) : null}
      </div>

      {open ? <div className="era-card__work" id={panelId}>
      <div className="era-card__controls">
      <Text strong style={{ display: 'block', marginBottom: 6 }}>
        Base prompt
      </Text>
      <Input.TextArea
        rows={3}
        value={basePrompt}
        onChange={(e) => setBasePrompt(e.target.value)}
        disabled={archived}
        placeholder="Subject lock / base prompt"
        aria-label="Base prompt"
        style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-body-sm)' }}
      />

      <div className="base-ref">
        <p className="base-ref__line">
          <Text strong>Base reference</Text>
          <Text type="secondary" className="base-ref__hint">
            {item.meta?.baseReferenceContentUrl
              ? 'Included for this character and pages that cast them.'
              : 'No photo yet. Included for this character and pages that cast them.'}
          </Text>
        </p>
        {item.meta?.baseReferenceContentUrl ? (
          <BaseReferenceImage
            contentUrl={item.meta.baseReferenceContentUrl}
            alt={`${item.title || 'Character'} base reference`}
          />
        ) : null}
        {!archived && (
          <div className="base-ref__actions">
            <Upload
              accept="image/png,image/jpeg,image/webp"
              showUploadList={false}
              disabled={uploadingRef}
              beforeUpload={(file) => {
                const okType = ['image/png', 'image/jpeg', 'image/webp'].includes(file.type);
                if (!okType) {
                  message.error('Upload a PNG, JPEG, or WebP image');
                  return false;
                }
                if (file.size > 8 * 1024 * 1024) {
                  message.error('Image must be under 8 MB');
                  return false;
                }
                setUploadingRef(true);
                setRefStatus('');
                const reader = new FileReader();
                reader.onload = async () => {
                  try {
                    const result = String(reader.result || '');
                    const comma = result.indexOf(',');
                    const base64 = comma >= 0 ? result.slice(comma + 1) : result;
                    await onUploadBaseReference?.(item.id, {
                      filename: file.name,
                      mimeType: file.type,
                      base64,
                    });
                    setRefStatus('Base reference saved. Generate will send this image.');
                    message.success('Base reference saved');
                    onUpdated?.();
                  } catch (e) {
                    setRefStatus('');
                    message.error(e.message || 'Upload failed');
                  } finally {
                    setUploadingRef(false);
                  }
                };
                reader.onerror = () => {
                  setUploadingRef(false);
                  message.error('Could not read that image');
                };
                reader.readAsDataURL(file);
                return false;
              }}
            >
              <Button
                htmlType="button"
                className="era-control"
                loading={uploadingRef}
                aria-label={`Upload base reference for ${item.title || 'character'}`}
              >
                {item.meta?.baseReferenceContentUrl ? 'Replace image' : 'Upload image'}
              </Button>
            </Upload>
            {item.meta?.baseReferenceContentUrl && (
              <Popconfirm
                title="Remove this base reference?"
                okText="Remove"
                cancelText="Cancel"
                onConfirm={async () => {
                  setUploadingRef(true);
                  try {
                    await onClearBaseReference?.(item.id);
                    setRefStatus('Base reference removed.');
                    message.success('Base reference removed');
                    onUpdated?.();
                  } catch (e) {
                    message.error(e.message || 'Could not remove the image');
                  } finally {
                    setUploadingRef(false);
                  }
                }}
              >
                <Button htmlType="button" className="era-control" disabled={uploadingRef}>
                  Remove
                </Button>
              </Popconfirm>
            )}
          </div>
        )}
        {refStatus ? (
          <p className="base-ref__status" role="status">
            {refStatus}
          </p>
        ) : null}
      </div>

      {!archived && (
        <>
          <Text strong style={{ display: 'block', marginTop: 12, marginBottom: 6 }}>
            Pose / action / state
          </Text>
          {kind === 'person' ? (
            <>
              <PoseChipGroup poses={[HEAD_POSE, STANDING_POSE]} selected={pose} onSelect={selectPose} />
              {activityPicker}
              {expressionPicker}
            </>
          ) : kind === 'dog' ? (
            <>
              <PoseChipGroup label="Sheet" poses={PERSON_SHEET_POSES} selected={pose} onSelect={selectPose} />
              {expressionPicker}
              <PoseChipGroup
                poses={poses.filter((extra) => extra.id !== 'face' && extra.label !== 'face')}
                selected={pose}
                onSelect={selectPose}
              />
            </>
          ) : (
            <PoseChipGroup
              poses={posesWithFlight(poses, item, kind)}
              selected={pose}
              onSelect={setPose}
            />
          )}
          {expressionId && (
            <div style={{ marginTop: 12, border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3)', background: 'var(--color-bg-surface)' }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 6 }}>
                Face · {expressions.find((entry) => entry.id === expressionId)?.label}
              </Text>
              <Input.TextArea
                value={overrideFragment}
                onChange={(e) => setOverrideFragment(e.target.value)}
                autoSize={{ minRows: 2 }}
                aria-label="Expression fragment override"
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 12, marginBottom: 6 }}>
                Mannerism · {item.title}
              </Text>
              <Input.TextArea
                value={mannerism}
                onChange={(e) => setMannerism(e.target.value)}
                autoSize={{ minRows: 2 }}
                placeholder="This character’s gesture"
                aria-label="Character mannerism"
              />
              <Text type="secondary" style={{ display: 'block', marginTop: 6 }}>
                {sharedMannerism ? `Empty keeps the shared line: ${sharedMannerism}. ` : 'Empty keeps the shared line. '}
                Standing and Activity use this save. Head stays neck up, face only.
              </Text>
              <Button size="small" style={{ marginTop: 8 }} onClick={() => saveOverride()}>Save on this character</Button>
            </div>
          )}
          <Input
            value={pose}
            onChange={(e) => {
              setExpressionId(null);
              setRowEmotion(null);
              setPose(e.target.value);
            }}
            placeholder="Pose / action / state (free text)"
            style={{ marginTop: 8 }}
            aria-label="Pose free text"
          />
          <Input
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            placeholder="Optional override for this attempt…"
            style={{ marginTop: 8 }}
            aria-label="Character generate override"
          />
          <div className="era-scene-row__actions">
            <Button
              htmlType="button"
              type="primary"
              className="era-control era-scene-row__generate"
              icon={<ThunderboltOutlined />}
              loading={busy}
              onClick={rowEmotion ? () => runExpressionRow(rowEmotion) : expressionId ? runExpression : runGenerate}
              aria-label={
                rowEmotion
                  ? `Generate ${rowEmotion} row for ${item.title}`
                  : `Generate illustration for ${item.title}`
              }
            >
              {busy ? 'Generating…' : rowEmotion ? 'Generate row' : 'Generate'}
            </Button>
          </div>
        </>
      )}

      {shownError && (
        <Alert type="error" showIcon style={{ marginTop: 10, whiteSpace: 'normal' }} title={shownError} />
      )}
      </div>

      <div className="era-card__gallery era-scene-row__gallery">
        {gallery.length === 0 ? (
          <div className="era-scene-row__preview-empty">No generation yet — pick a pose and press Generate.</div>
        ) : (
          <>
            <Text type="secondary" className="era-scene-row__gallery-label">
              {gallery.length} attempt{gallery.length === 1 ? '' : 's'} · newest first · Lock
            </Text>
            <ul className="era-scene-gallery">
              {gallery.map((g) => (
                <li key={g.id} className="era-scene-gallery__item">
                  <GenerationThumb
                    projectId={projectId}
                    generation={g}
                    alt={`${item.title} · ${g.id}`}
                    caption={`${item.title} · ${g.pose || pose || 'pose'}${g.lockedAsReference ? ' · locked' : ''}`}
                    className="generation-thumb--gallery"
                  />
                  <GenerationAttemptActions
                    generation={g}
                    approvingId={approvingId}
                    unlockingId={unlockingId}
                    deletingId={deletingId}
                    onApprove={onApprove}
                    onUnlock={onUnlockGeneration}
                    onDelete={
                      onDeleteGeneration
                        ? async (gen) => {
                            await onDeleteGeneration(gen);
                            if (localGeneration?.id === gen.id) setLocalGeneration(null);
                          }
                        : undefined
                    }
                    onExportGeneration={onExportGeneration}
                    exportingId={exportingId}
                    onReuseOverride={
                      g.override ? () => setOverride(g.override) : undefined
                    }
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
      {!archived ? (
        <div className="era-card__save">
          <Button htmlType="button" type="primary" className="era-control" loading={saving} onClick={save}>
            Save
          </Button>
        </div>
      ) : null}
      </div> : null}
    </section>
  );
}

function CharactersSection({
  projectId,
  items,
  chapters,
  generations,
  expressionCatalog,
  onStructureChange,
  onAdd,
  onUpdate,
  onArchive,
  onRestore,
  onDelete,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
  onGenerated,
  onUpdated,
  onUploadBaseReference,
  onClearBaseReference,
  bibleMatch,
}) {
  const [adding, setAdding] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [summaryValue, setSummaryValue] = useState('');
  const [kindValue, setKindValue] = useState('person');
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const active = (items || []).filter((item) => !isArchivedItem(item));
  const archived = (items || []).filter((item) => isArchivedItem(item));
  const visible = showArchived ? archived : active;
  const { groups, rest } = groupCharactersByChapter(
    visible,
    (chapters || []).filter((chapter) => isArchivedItem(chapter) === showArchived)
  );
  const grouped = groups.length > 0;

  const submit = async () => {
    if (!titleValue.trim()) {
      message.warning('Title is required');
      return;
    }
    setSaving(true);
    try {
      await onAdd({
        title: titleValue.trim(),
        summary: summaryValue,
        meta: { kind: kindValue, basePrompt: summaryValue, poses: DEFAULT_POSES[kindValue] },
      });
      setTitleValue('');
      setSummaryValue('');
      setKindValue('person');
      setAdding(false);
      message.success('Added');
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="characters-workspace">
      <div className="flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <Paragraph style={{ color: 'var(--color-text-secondary)', marginBottom: 0, maxWidth: 'var(--measure-prose)' }}>
          People, dogs, van, and tree — base prompt, a base reference photo, then Generate and Lock.
        </Paragraph>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {archived.length > 0 && (
            <Button type="link" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? 'Show active' : `Archived (${archived.length})`}
            </Button>
          )}
          {!showArchived && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
              Add character
            </Button>
          )}
        </div>
      </div>

      {adding && !showArchived && (
        <div
          style={{
            padding: 'var(--space-4)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxWidth: 520,
          }}
        >
          <Select
            value={kindValue}
            onChange={setKindValue}
            options={[
              { value: 'person', label: 'Person' },
              { value: 'dog', label: 'Dog' },
              { value: 'van', label: 'Van' },
              { value: 'tree', label: 'Tree' },
            ]}
            aria-label="Character kind"
          />
          <Input
            placeholder="Title"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            autoFocus
          />
          <Input.TextArea
            rows={3}
            placeholder="Base prompt / notes"
            value={summaryValue}
            onChange={(e) => setSummaryValue(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="primary" loading={saving} onClick={submit}>
              Create
            </Button>
            <Button
              onClick={() => {
                setAdding(false);
                setTitleValue('');
                setSummaryValue('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {grouped && (
        <ChapterJump chapters={groups.map((group) => group.chapter)} idPrefix="characters-" />
      )}

      {!visible.length ? (
        <Empty description={showArchived ? 'No archived characters.' : 'No characters yet — use + Add.'} />
      ) : grouped ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
          {groups.map((group) => (
            <section
              key={group.chapter.id}
              id={`characters-${group.chapter.id}`}
              className="chapter-anchor"
              aria-label={chapterJumpLabel(group.chapter)}
            >
              <h2 className="chapter-anchor__title">{chapterJumpLabel(group.chapter)}</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {group.characters.map((item) => (
                  <CharacterCard
                    key={item.id}
                    item={item}
                    projectId={projectId}
                    generations={generations}
                    expressionCatalog={expressionCatalog}
                    onStructureChange={onStructureChange}
                    onUpdate={(body) => onUpdate(item.id, body)}
                    onArchive={() => onArchive(item.id)}
                    onRestore={() => onRestore(item.id)}
                    onDelete={() => onDelete(item.id)}
                    onApprove={onApprove}
                    approvingId={approvingId}
                    onUnlockGeneration={onUnlockGeneration}
                    unlockingId={unlockingId}
                    onDeleteGeneration={onDeleteGeneration}
                    deletingId={deletingId}
                    onExportGeneration={onExportGeneration}
                    exportingId={exportingId}
                    onGenerated={onGenerated}
                    onUpdated={onUpdated}
                    onUploadBaseReference={onUploadBaseReference}
                    onClearBaseReference={onClearBaseReference}
                    matchStatus={bibleMatch?.characters?.[item.id]}
                    matchVersion={bibleMatch?.version}
                  />
                ))}
              </div>
            </section>
          ))}
          {rest.length > 0 && (
            <section id="characters-unassigned" className="chapter-anchor" aria-label="Other characters">
              <h2 className="chapter-anchor__title">Other</h2>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
                {rest.map((item) => (
                  <CharacterCard
                    key={item.id}
                    item={item}
                    projectId={projectId}
                    generations={generations}
                    expressionCatalog={expressionCatalog}
                    onStructureChange={onStructureChange}
                    onUpdate={(body) => onUpdate(item.id, body)}
                    onArchive={() => onArchive(item.id)}
                    onRestore={() => onRestore(item.id)}
                    onDelete={() => onDelete(item.id)}
                    onApprove={onApprove}
                    approvingId={approvingId}
                    onUnlockGeneration={onUnlockGeneration}
                    unlockingId={unlockingId}
                    onDeleteGeneration={onDeleteGeneration}
                    deletingId={deletingId}
                    onExportGeneration={onExportGeneration}
                    exportingId={exportingId}
                    onGenerated={onGenerated}
                    onUpdated={onUpdated}
                    onUploadBaseReference={onUploadBaseReference}
                    onClearBaseReference={onClearBaseReference}
                    matchStatus={bibleMatch?.characters?.[item.id]}
                    matchVersion={bibleMatch?.version}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
          {visible.map((item) => (
            <CharacterCard
              key={item.id}
              item={item}
              projectId={projectId}
              generations={generations}
              expressionCatalog={expressionCatalog}
              onStructureChange={onStructureChange}
              onUpdate={(body) => onUpdate(item.id, body)}
              onArchive={() => onArchive(item.id)}
              onRestore={() => onRestore(item.id)}
              onDelete={() => onDelete(item.id)}
              onApprove={onApprove}
              approvingId={approvingId}
              onUnlockGeneration={onUnlockGeneration}
              unlockingId={unlockingId}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
              onGenerated={onGenerated}
              onUpdated={onUpdated}
              onUploadBaseReference={onUploadBaseReference}
              onClearBaseReference={onClearBaseReference}
              matchStatus={bibleMatch?.characters?.[item.id]}
              matchVersion={bibleMatch?.version}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function OtherItemCard({
  item,
  projectId,
  generations,
  onUpdate,
  onArchive,
  onRestore,
  onDelete,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
  onGenerated,
  onUpdated,
  onUploadBaseReference,
  onClearBaseReference,
  matchStatus,
  matchVersion,
}) {
  const archived = isArchivedItem(item);
  const seek = isSeekAndFindItem(item);
  const [titleValue, setTitleValue] = useState(item.title || '');
  const [summaryValue, setSummaryValue] = useState(item.summary || '');
  const [placement, setPlacement] = useState(item.meta?.placement || '');
  const [override, setOverride] = useState('');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingRef, setUploadingRef] = useState(false);
  const [refStatus, setRefStatus] = useState('');
  const [localError, setLocalError] = useState(null);
  const [localGeneration, setLocalGeneration] = useState(null);

  useEffect(() => {
    setTitleValue(item.title || '');
    setSummaryValue(item.summary || '');
    setPlacement(item.meta?.placement || '');
  }, [item.id, item.title, item.summary, item.meta?.placement]);

  const matchesOther = (g) =>
    !g?.error &&
    g?.output &&
    (hasPicture(g)) &&
    g.otherId === item.id;

  const gallery = (generations || []).filter(matchesOther);
  if (localGeneration && matchesOther(localGeneration) && !gallery.some((g) => g.id === localGeneration.id)) {
    gallery.unshift(localGeneration);
  }
  const shownError = displayGenerateError(
    localError,
    latestGenerateError(generations, (g) => g.otherId === item.id)
  );

  const save = async () => {
    if (!titleValue.trim()) {
      message.warning('Name is required');
      return;
    }
    setSaving(true);
    try {
      await onUpdate({
        title: titleValue.trim(),
        summary: summaryValue,
        prompt: summaryValue,
        meta: {
          ...(item.meta || {}),
          kind: item.meta?.kind || (seek ? 'seek-and-find' : undefined),
          placement,
          basePrompt: summaryValue,
        },
      });
      message.success('Saved');
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const runGenerate = async () => {
    if (busy || archived) return;
    setBusy(true);
    setLocalError(null);
    try {
      await onUpdate({
        title: titleValue.trim() || item.title,
        summary: summaryValue,
        prompt: summaryValue,
        meta: {
          ...(item.meta || {}),
          kind: item.meta?.kind || 'seek-and-find',
          placement,
          basePrompt: summaryValue,
        },
      });
      const data = await generateScene(projectId, {
        otherId: item.id,
        override: override.trim() || undefined,
        target: 'other',
        providerId: 'openai',
      });
      const generation = data.generation;
      if (generation?.error) {
        setLocalError(generation.error);
        message.error(generation.error);
      } else {
        setLocalGeneration(generation);
        message.success(`Generated “${item.title}”`);
        onGenerated?.(generation);
      }
    } catch (e) {
      const msg = await resolveGenerateError(projectId, e);
      setLocalError(msg);
      message.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className={`era-card${archived ? ' era-card--archived' : ''}`}>
      <div className="era-card__header">
        <div className="era-card__title-block">
          <Text type="secondary" className="era-card__meta">
            {seek ? <Tag color="blue">seek-and-find</Tag> : <Tag>other</Tag>}
            {archived ? ' · Archived' : ''}
            <BibleMatchChip status={matchStatus} version={matchVersion} />
          </Text>
          <Input
            className="era-card__title-input"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            disabled={archived}
            placeholder="Name"
            aria-label="Other item name"
          />
        </div>
        <div className="era-card__actions">
          <DeleteConfirmButton
            label="Delete"
            ariaLabel={`Delete ${item.title}`}
            onConfirm={async () => {
              try {
                await onDelete();
                message.success('Item deleted');
                onUpdated?.();
              } catch (e) {
                message.error(e.message);
                throw e;
              }
            }}
          />
          <div className="era-card__actions-end">
            {archived ? (
              <Button
                htmlType="button"
                className="era-control"
                onClick={async () => {
                  await onRestore();
                  message.success('Restored');
                  onUpdated?.();
                }}
              >
                Restore
              </Button>
            ) : (
              <Button
                htmlType="button"
                className="era-control"
                onClick={async () => {
                  await onArchive();
                  message.success('Archived');
                  onUpdated?.();
                }}
              >
                Archive
              </Button>
            )}
            {!archived && (
              <Button htmlType="button" type="primary" className="era-control" loading={saving} onClick={save}>
                Save
              </Button>
            )}
          </div>
        </div>
      </div>

      <Text strong style={{ display: 'block', marginBottom: 6 }}>
        Description
      </Text>
      <Input.TextArea
        rows={3}
        value={summaryValue}
        onChange={(e) => setSummaryValue(e.target.value)}
        disabled={archived}
        placeholder="Description / summary"
        style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-body-sm)' }}
      />
      <Text strong style={{ display: 'block', marginTop: 12, marginBottom: 6 }}>
        Chapter / page placement
      </Text>
      <Input.TextArea
        rows={2}
        value={placement}
        onChange={(e) => setPlacement(e.target.value)}
        disabled={archived}
        placeholder="Where this secret appears (chapter / page notes)"
      />

      <div className="base-ref">
        <Text strong>Reference</Text>
        <Text type="secondary" className="base-ref__hint">
          Upload a photo. Generate sends it with this item, and with any page that includes it.
        </Text>
        {item.meta?.baseReferenceContentUrl ? (
          <BaseReferenceImage
            contentUrl={item.meta.baseReferenceContentUrl}
            alt={`${item.title || 'Item'} reference`}
          />
        ) : (
          <Text type="secondary">No reference yet.</Text>
        )}
        {!archived && (
          <div className="base-ref__actions">
            <Upload
              accept="image/png,image/jpeg,image/webp"
              showUploadList={false}
              disabled={uploadingRef}
              beforeUpload={(file) => {
                const okType = ['image/png', 'image/jpeg', 'image/webp'].includes(file.type);
                if (!okType) {
                  message.error('Upload a PNG, JPEG, or WebP image');
                  return false;
                }
                if (file.size > 8 * 1024 * 1024) {
                  message.error('Image must be under 8 MB');
                  return false;
                }
                setUploadingRef(true);
                setRefStatus('');
                const reader = new FileReader();
                reader.onload = async () => {
                  try {
                    const result = String(reader.result || '');
                    const comma = result.indexOf(',');
                    const base64 = comma >= 0 ? result.slice(comma + 1) : result;
                    await onUploadBaseReference?.(item.id, {
                      filename: file.name,
                      mimeType: file.type,
                      base64,
                    });
                    setRefStatus('Reference saved. Generate will send this image.');
                    message.success('Reference saved');
                    onUpdated?.();
                  } catch (e) {
                    setRefStatus('');
                    message.error(e.message || 'Upload failed');
                  } finally {
                    setUploadingRef(false);
                  }
                };
                reader.onerror = () => {
                  setUploadingRef(false);
                  message.error('Could not read that image');
                };
                reader.readAsDataURL(file);
                return false;
              }}
            >
              <Button
                htmlType="button"
                className="era-control"
                loading={uploadingRef}
                aria-label={`Upload reference for ${item.title || 'item'}`}
              >
                {item.meta?.baseReferenceContentUrl ? 'Replace image' : 'Upload image'}
              </Button>
            </Upload>
            {item.meta?.baseReferenceContentUrl && (
              <Popconfirm
                title="Remove this reference?"
                okText="Remove"
                cancelText="Cancel"
                onConfirm={async () => {
                  setUploadingRef(true);
                  try {
                    await onClearBaseReference?.(item.id);
                    setRefStatus('Reference removed.');
                    message.success('Reference removed');
                    onUpdated?.();
                  } catch (e) {
                    message.error(e.message || 'Could not remove the image');
                  } finally {
                    setUploadingRef(false);
                  }
                }}
              >
                <Button htmlType="button" className="era-control" disabled={uploadingRef}>
                  Remove
                </Button>
              </Popconfirm>
            )}
          </div>
        )}
        {refStatus ? (
          <p className="base-ref__status" role="status">
            {refStatus}
          </p>
        ) : null}
      </div>

      {!archived && (
        <>
          <Input
            value={override}
            onChange={(e) => setOverride(e.target.value)}
            placeholder="Optional generate override…"
            style={{ marginTop: 12 }}
          />
          <div className="era-scene-row__actions">
            <Button
              htmlType="button"
              type="primary"
              className="era-control era-scene-row__generate"
              icon={<ThunderboltOutlined />}
              loading={busy}
              onClick={runGenerate}
            >
              {busy ? 'Generating…' : 'Generate'}
            </Button>
          </div>
        </>
      )}

      {shownError && (
        <Alert type="error" showIcon style={{ marginTop: 10, whiteSpace: 'normal' }} title={shownError} />
      )}

      <div className="era-scene-row__gallery" style={{ marginTop: 16, borderBottom: 'none', paddingBottom: 0 }}>
        {gallery.length === 0 ? (
          <div className="era-scene-row__preview-empty">No secret-prop generation yet.</div>
        ) : (
          <ul className="era-scene-gallery">
            {gallery.map((g) => (
              <li key={g.id} className="era-scene-gallery__item">
                <GenerationThumb
                  projectId={projectId}
                  generation={g}
                  alt={`${item.title} · ${g.id}`}
                  caption={`${item.title}${g.lockedAsReference ? ' · locked' : ''}`}
                  className="generation-thumb--gallery"
                />
                <GenerationAttemptActions
                  generation={g}
                  approvingId={approvingId}
                  unlockingId={unlockingId}
                  deletingId={deletingId}
                  onApprove={onApprove}
                  onUnlock={onUnlockGeneration}
                  onDelete={
                    onDeleteGeneration
                      ? async (gen) => {
                          await onDeleteGeneration(gen);
                          if (localGeneration?.id === gen.id) setLocalGeneration(null);
                        }
                      : undefined
                  }
                  onExportGeneration={onExportGeneration}
                  exportingId={exportingId}
                />
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}

function OtherSection({
  projectId,
  items,
  chapters,
  generations,
  onAdd,
  onUpdate,
  onArchive,
  onRestore,
  onDelete,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
  onGenerated,
  onUpdated,
  onUploadBaseReference,
  onClearBaseReference,
  bibleMatch,
}) {
  const [adding, setAdding] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [summaryValue, setSummaryValue] = useState('');
  const [placementValue, setPlacementValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showAll, setShowAll] = useState(false);

  const active = (items || []).filter((item) => !isArchivedItem(item));
  const archived = (items || []).filter((item) => isArchivedItem(item));
  const pool = showArchived ? archived : active;
  const seekItems = pool.filter(isSeekAndFindItem);
  const visible = showAll || showArchived ? pool : seekItems.length ? seekItems : pool;
  const { groups, rest } = groupOthersByChapter(
    visible,
    (chapters || []).filter((chapter) => isArchivedItem(chapter) === showArchived)
  );
  const jumpChapters = [
    ...groups.map((group) => group.chapter),
    ...(rest.length ? [{ id: 'unassigned', title: 'Unassigned' }] : []),
  ];
  const grouped = groups.length > 0;

  const submit = async () => {
    if (!titleValue.trim()) {
      message.warning('Name is required');
      return;
    }
    setSaving(true);
    try {
      await onAdd({
        title: titleValue.trim(),
        summary: summaryValue,
        meta: { kind: 'seek-and-find', placement: placementValue },
      });
      setTitleValue('');
      setSummaryValue('');
      setPlacementValue('');
      setAdding(false);
      message.success('Added');
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="other-workspace" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
      <div className="flex items-center justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <Paragraph style={{ color: 'var(--color-text-secondary)', marginBottom: 0, maxWidth: 'var(--measure-prose)' }}>
          Seek-and-find secrets — name, description, placement notes, and a reference photo. Generate and Lock when you want a drawn version.
        </Paragraph>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!showArchived && seekItems.length > 0 && seekItems.length < active.length && (
            <Button type="link" onClick={() => setShowAll((v) => !v)}>
              {showAll ? 'Seek-and-find only' : `Show all (${active.length})`}
            </Button>
          )}
          {archived.length > 0 && (
            <Button type="link" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? 'Show active' : `Archived (${archived.length})`}
            </Button>
          )}
          {!showArchived && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
              Add
            </Button>
          )}
        </div>
      </div>

      {adding && !showArchived && (
        <div
          style={{
            padding: 'var(--space-4)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius-md)',
            background: 'var(--color-bg-surface)',
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            maxWidth: 520,
          }}
        >
          <Input placeholder="Name" value={titleValue} onChange={(e) => setTitleValue(e.target.value)} autoFocus />
          <Input.TextArea
            rows={3}
            placeholder="Description"
            value={summaryValue}
            onChange={(e) => setSummaryValue(e.target.value)}
          />
          <Input.TextArea
            rows={2}
            placeholder="Chapter / page placement notes"
            value={placementValue}
            onChange={(e) => setPlacementValue(e.target.value)}
          />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button type="primary" loading={saving} onClick={submit}>
              Create
            </Button>
            <Button
              onClick={() => {
                setAdding(false);
                setTitleValue('');
                setSummaryValue('');
                setPlacementValue('');
              }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {grouped && <ChapterJump chapters={jumpChapters} idPrefix="other-" />}

      {!visible.length ? (
        <Empty description={showArchived ? 'No archived items.' : 'No seek-and-find items yet — use + Add.'} />
      ) : grouped ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)' }}>
          {groups.map((group) => (
            <section
              key={group.chapter.id}
              id={`other-${group.chapter.id}`}
              className="chapter-anchor"
              aria-label={chapterJumpLabel(group.chapter)}
            >
              <h2 className="chapter-anchor__title">{chapterJumpLabel(group.chapter)}</h2>
              <div className="other-list">
                {group.items.map((item) => (
                  <OtherItemCard
                    key={item.id}
                    item={item}
                    projectId={projectId}
                    generations={generations}
                    onUpdate={(body) => onUpdate(item.id, body)}
                    onArchive={() => onArchive(item.id)}
                    onRestore={() => onRestore(item.id)}
                    onDelete={() => onDelete(item.id)}
                    onApprove={onApprove}
                    approvingId={approvingId}
                    onUnlockGeneration={onUnlockGeneration}
                    unlockingId={unlockingId}
                    onDeleteGeneration={onDeleteGeneration}
                    deletingId={deletingId}
                    onExportGeneration={onExportGeneration}
                    exportingId={exportingId}
                    onGenerated={onGenerated}
                    onUpdated={onUpdated}
                    onUploadBaseReference={onUploadBaseReference}
                    onClearBaseReference={onClearBaseReference}
                    matchStatus={bibleMatch?.others?.[item.id]}
                    matchVersion={bibleMatch?.version}
                  />
                ))}
              </div>
            </section>
          ))}
          {rest.length > 0 && (
            <section id="other-unassigned" className="chapter-anchor" aria-label="Unassigned">
              <h2 className="chapter-anchor__title">Unassigned</h2>
              <div className="other-list">
                {rest.map((item) => (
                  <OtherItemCard
                    key={item.id}
                    item={item}
                    projectId={projectId}
                    generations={generations}
                    onUpdate={(body) => onUpdate(item.id, body)}
                    onArchive={() => onArchive(item.id)}
                    onRestore={() => onRestore(item.id)}
                    onDelete={() => onDelete(item.id)}
                    onApprove={onApprove}
                    approvingId={approvingId}
                    onUnlockGeneration={onUnlockGeneration}
                    unlockingId={unlockingId}
                    onDeleteGeneration={onDeleteGeneration}
                    deletingId={deletingId}
                    onExportGeneration={onExportGeneration}
                    exportingId={exportingId}
                    onGenerated={onGenerated}
                    onUpdated={onUpdated}
                    onUploadBaseReference={onUploadBaseReference}
                    onClearBaseReference={onClearBaseReference}
                    matchStatus={bibleMatch?.others?.[item.id]}
                    matchVersion={bibleMatch?.version}
                  />
                ))}
              </div>
            </section>
          )}
        </div>
      ) : (
        <div className="other-list">
          {visible.map((item) => (
            <OtherItemCard
              key={item.id}
              item={item}
              projectId={projectId}
              generations={generations}
              onUpdate={(body) => onUpdate(item.id, body)}
              onArchive={() => onArchive(item.id)}
              onRestore={() => onRestore(item.id)}
              onDelete={() => onDelete(item.id)}
              onApprove={onApprove}
              approvingId={approvingId}
              onUnlockGeneration={onUnlockGeneration}
              unlockingId={unlockingId}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
              onExportGeneration={onExportGeneration}
              exportingId={exportingId}
              onGenerated={onGenerated}
              onUpdated={onUpdated}
              onUploadBaseReference={onUploadBaseReference}
              onClearBaseReference={onClearBaseReference}
              matchStatus={bibleMatch?.others?.[item.id]}
              matchVersion={bibleMatch?.version}
            />
          ))}
        </div>
      )}
    </div>
  );
}

function ChapterSeedFromDocument({ projectId, chapter, onSeeded }) {
  const [documents, setDocuments] = useState([]);
  const [sections, setSections] = useState([{ id: '__full__', title: 'Entire document' }]);
  const [documentId, setDocumentId] = useState(null);
  const [section, setSection] = useState('__full__');
  const [loadingDocs, setLoadingDocs] = useState(true);
  const [seeding, setSeeding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingDocs(true);
      try {
        const data = await listDocuments(projectId);
        if (cancelled) return;
        const docs = data.documents || [];
        setDocuments(docs);
        // Prefer Adventure Awaits morning notes when present
        const preferred =
          docs.find((d) => /chapter-1|morning/i.test(d.name)) || docs[0] || null;
        if (preferred) setDocumentId(preferred.id);
      } catch {
        if (!cancelled) setDocuments([]);
      } finally {
        if (!cancelled) setLoadingDocs(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId]);

  useEffect(() => {
    if (!documentId) {
      setSections([{ id: '__full__', title: 'Entire document' }]);
      setSection('__full__');
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await listDocumentSections(projectId, documentId);
        if (cancelled) return;
        const next = data.sections || [{ id: '__full__', title: 'Entire document' }];
        setSections(next);
        const intent = next.find((s) => /intent/i.test(s.title));
        setSection(intent ? intent.title : '__full__');
      } catch {
        if (!cancelled) {
          setSections([{ id: '__full__', title: 'Entire document' }]);
          setSection('__full__');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [projectId, documentId]);

  const onSeed = async () => {
    if (!documentId) {
      message.warning('Upload a Project Document first');
      return;
    }
    setSeeding(true);
    try {
      const result = await seedChapterFromDocument(projectId, chapter.id, {
        documentId,
        section: section === '__full__' ? null : section,
      });
      message.success(`Seeded “${chapter.title}” from ${result.document?.name || 'document'}`);
      onSeeded?.(result);
    } catch (e) {
      message.error(e.message);
    } finally {
      setSeeding(false);
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        marginTop: 'var(--space-4)',
        paddingTop: 'var(--space-4)',
        borderTop: '1px solid var(--color-border-subtle)',
      }}
    >
      <Text strong>Seed treatment from Project Document</Text>
      <Text type="secondary" style={{ fontSize: 'var(--text-caption)' }}>
        Pull prompt text from an uploaded .md (not the Illustration Bible). Optional: use a ## section.
      </Text>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-3)', alignItems: 'flex-end' }}>
        <div style={{ minWidth: 200, flex: 1 }}>
          <Text type="secondary" style={{ fontSize: 'var(--text-caption)', display: 'block', marginBottom: 4 }}>
            Document
          </Text>
          <Select
            style={{ width: '100%' }}
            loading={loadingDocs}
            placeholder={loadingDocs ? 'Loading…' : 'Choose a .md document'}
            value={documentId}
            onChange={setDocumentId}
            options={documents.map((d) => ({ value: d.id, label: d.name }))}
            notFoundContent="No documents — upload under Project Documents"
          />
        </div>
        <div style={{ minWidth: 180, flex: 1 }}>
          <Text type="secondary" style={{ fontSize: 'var(--text-caption)', display: 'block', marginBottom: 4 }}>
            Section
          </Text>
          <Select
            style={{ width: '100%' }}
            value={section}
            onChange={setSection}
            options={sections.map((s) => ({
              value: s.id === '__full__' ? '__full__' : s.title,
              label: s.title,
            }))}
          />
        </div>
        <Button type="primary" loading={seeding} onClick={onSeed} disabled={!documentId}>
          Set chapter treatment
        </Button>
      </div>
      {chapter.sourceDocument?.name && (
        <Text type="secondary" style={{ fontSize: 'var(--text-caption)' }}>
          Last seeded from {chapter.sourceDocument.name}
          {chapter.sourceDocument.section ? ` · ${chapter.sourceDocument.section}` : ''}
        </Text>
      )}
    </div>
  );
}

function ChapterCard({ projectId, chapter, index, onUpdated }) {
  const [titleValue, setTitleValue] = useState(chapter.title || '');
  const [prompt, setPrompt] = useState(chapter.prompt || '');
  const [savingPrompt, setSavingPrompt] = useState(false);
  const [addingPage, setAddingPage] = useState(false);
  const [pageTitle, setPageTitle] = useState('');
  const [savingPage, setSavingPage] = useState(false);
  const [busy, setBusy] = useState(false);
  const archived = isArchivedItem(chapter);

  useEffect(() => {
    setTitleValue(chapter.title || '');
    setPrompt(chapter.prompt || '');
  }, [chapter.id, chapter.title, chapter.prompt]);

  const savePrompt = async () => {
    if (!titleValue.trim()) {
      message.warning('Chapter title required');
      return;
    }
    setSavingPrompt(true);
    try {
      await updateChapter(projectId, chapter.id, { title: titleValue.trim(), prompt });
      message.success('Chapter saved');
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSavingPrompt(false);
    }
  };

  const createPage = async () => {
    if (!pageTitle.trim()) {
      message.warning('Page title required');
      return;
    }
    setSavingPage(true);
    try {
      await addPage(projectId, chapter.id, { title: pageTitle.trim() });
      message.success('Page added');
      setPageTitle('');
      setAddingPage(false);
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSavingPage(false);
    }
  };

  const pageCount = (chapter.pages || []).filter((p) => !isArchivedItem(p)).length;

  const onArchiveToggle = async () => {
    setBusy(true);
    try {
      if (archived) {
        await restoreChapter(projectId, chapter.id);
        message.success('Chapter restored');
      } else {
        await archiveChapter(projectId, chapter.id);
        message.success('Chapter archived');
      }
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      const result = await deleteChapter(projectId, chapter.id);
      message.success(
        result?.pagesRemoved
          ? `Deleted chapter and ${result.pagesRemoved} page${result.pagesRemoved === 1 ? '' : 's'}`
          : 'Chapter deleted'
      );
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  return (
    <section
      style={{
        padding: 'var(--space-5)',
        background: archived ? 'var(--color-bg-sunken)' : 'var(--color-bg-surface)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--shadow-sm)',
        opacity: archived ? 0.9 : 1,
      }}
    >
      <div className="flex items-start justify-between gap-3" style={{ marginBottom: 4 }}>
        <Text type="secondary" style={{ fontSize: 'var(--text-caption)', letterSpacing: 'var(--tracking-label)' }}>
          CHAPTER {index + 1}
          {archived ? ' · ARCHIVED' : ''}
        </Text>
        <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
          <Button
            type="text"
            size="small"
            icon={<InboxOutlined />}
            loading={busy}
            onClick={onArchiveToggle}
          >
            {archived ? 'Restore' : 'Archive'}
          </Button>
          <DeleteConfirmButton
            type="text"
            label="Delete"
            loading={busy}
            ariaLabel={`Delete chapter ${chapter.title}`}
            onConfirm={handleDelete}
          />
        </div>
      </div>
      <Input
        value={titleValue}
        onChange={(e) => setTitleValue(e.target.value)}
        disabled={archived}
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title)',
          fontWeight: 600,
          marginBottom: 8,
        }}
      />
      <Text strong style={{ display: 'block', marginBottom: 6 }}>
        Treatment / prompt (Markdown)
      </Text>
      <Input.TextArea
        rows={6}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        placeholder="Chapter treatment / prompt — edit freely or seed from a Project Document"
        style={{ fontFamily: 'var(--font-mono)', fontSize: 'var(--text-body-sm)' }}
        disabled={archived}
      />
      {!archived && (
        <Button type="primary" loading={savingPrompt} onClick={savePrompt} style={{ marginTop: 8 }}>
          Save chapter
        </Button>
      )}

      {!archived && (
        <div style={{ marginTop: 'var(--space-4)' }}>
          <div className="flex items-center justify-between gap-3" style={{ marginBottom: 8 }}>
            <Text type="secondary" style={{ fontSize: 'var(--text-caption)' }}>
              {pageCount} active page{pageCount === 1 ? '' : 's'}
            </Text>
            <Button size="small" icon={<PlusOutlined />} onClick={() => setAddingPage(true)}>
              Add page
            </Button>
          </div>
          {(chapter.pages || [])
            .filter((p) => !isArchivedItem(p))
            .map((p) => (
              <div
                key={p.id}
                style={{ padding: '6px 0', color: 'var(--color-text-secondary)', fontSize: 'var(--text-body-sm)' }}
              >
                {p.title} · {p.status || 'working'} · {p.workDpi || 72}/{p.exportDpi || 300} dpi
              </div>
            ))}
          {addingPage && (
            <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
              <Input
                placeholder="Page title"
                value={pageTitle}
                onChange={(e) => setPageTitle(e.target.value)}
                style={{ maxWidth: 280 }}
              />
              <Button type="primary" loading={savingPage} onClick={createPage}>
                Create page
              </Button>
              <Button onClick={() => setAddingPage(false)}>Cancel</Button>
            </div>
          )}
        </div>
      )}

      {!archived && <ChapterSeedFromDocument projectId={projectId} chapter={chapter} onSeeded={onUpdated} />}
    </section>
  );
}

function EraCastChecklist({ cast }) {
  if (!cast?.length) {
    return <Alert type="warning" showIcon title="Cast checklist missing — add required subjects so scan/assign cannot drop people." />;
  }
  const missing = cast.filter((c) => c.required !== false && !c.checked);
  return (
    <div className="era-cast">
      <Text strong style={{ display: 'block', marginBottom: 8 }}>
        Cast checklist
      </Text>
      <ul className="era-cast__list">
        {cast.map((member) => (
          <li key={member.role || member.characterId || member.label} className="era-cast__item">
            <span className="era-cast__check" aria-hidden="true">
              {member.checked === false ? '○' : '✓'}
            </span>
            <div>
              <Text strong>{member.label}</Text>
              {member.age != null && (
                <Text type="secondary"> · {member.age} (b.{member.birthYear})</Text>
              )}
              {member.focus === 'background' && <Tag style={{ marginInlineStart: 8 }}>background</Tag>}
              {member.introduced?.length > 0 &&
                member.introduced.map((item) => (
                  <Tag color="blue" key={item} style={{ marginInlineStart: 8 }}>
                    introduced: {item}
                  </Tag>
                ))}
              {member.appearance && (
                <div>
                  <Text type="secondary">{member.appearance}</Text>
                </div>
              )}
            </div>
          </li>
        ))}
      </ul>
      {missing.length > 0 && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 8 }}
          title={`${missing.length} required cast member(s) unchecked`}
        />
      )}
    </div>
  );
}

/** Text Generate follows for this page. A complete bible scene is promptBody. */
function pageSceneText(page, scene) {
  if (page?.promptIsComplete && String(page.promptBody || '').trim()) return page.promptBody;
  return page?.scenePrompt || page?.notes || page?.prompt || scene?.prompt || scene?.summary || '';
}

function scenePromptFromBody(text) {
  const body = String(text || '').trim();
  const match = /(?:^|\n)SCENE:\s*([\s\S]*?)(?=\n(?:FRAMING|REJECTION CHECKLIST|LAYOUT)\b|$)/i.exec(
    body
  );
  return (match ? match[1] : body).trim();
}

function pageScenePatch(page, text) {
  const value = String(text || '');
  if (page?.promptIsComplete && String(page.promptBody || '').trim()) {
    return { promptBody: value, scenePrompt: scenePromptFromBody(value) };
  }
  return { scenePrompt: value };
}

function EraSceneRow({
  projectId,
  era,
  scene,
  page: pageProp,
  characters,
  others,
  bibleVersion,
  archived,
  generations,
  onUpdated,
  onGenerated,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
  matchStatus,
  matchVersion,
}) {
  const page = pageProp || scene?.pages?.[0] || null;
  const storedPrompt = pageSceneText(page, scene);
  const [promptValue, setPromptValue] = useState(storedPrompt);
  const pageSceneId = page?.id || scene?.id;
  const [promptOpen, setPromptOpen] = useAccordionOpen(
    pageSceneId ? accordionKey('page-scene', projectId, pageSceneId) : null
  );
  const [override, setOverride] = useState('');
  const [castPrefixes, setCastPrefixes] = useState([]);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [localError, setLocalError] = useState(null);
  const [localInfo, setLocalInfo] = useState(null);
  const [localGeneration, setLocalGeneration] = useState(null);

  const pageTitle = page?.title || scene?.title || 'Page';
  const cast = castStillInBook(era?.cast, characters);
  const pageOthers = othersForPage(others, page, era);
  const onPageIds = new Set(pageOthers.map((item) => item.id));
  const addableOthers = (others || []).filter(
    (item) =>
      !isArchivedItem(item) &&
      isSeekAndFindItem(item) &&
      !onPageIds.has(item.id) &&
      (era?.year == null || item.meta?.year == null || yearsAreSameChapter(item.meta.year, era.year))
  );

  const changePageOthers = async (itemId, action) => {
    if (!page?.id || archived) return;
    setSaving(true);
    try {
      await updatePage(projectId, page.id, pageOtherPatch(page, itemId, action));
      onUpdated?.();
    } catch (e) {
      setLocalError(e.message);
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    setPromptValue(pageSceneText(page, scene));
  }, [
    page?.id,
    page?.promptBody,
    page?.promptIsComplete,
    page?.scenePrompt,
    page?.notes,
    page?.prompt,
    scene?.id,
    scene?.prompt,
    scene?.summary,
  ]);

  const saveTreatment = async () => {
    if (!scene?.id) {
      if (page?.id) {
        setSaving(true);
        try {
          await updatePage(projectId, page.id, pageScenePatch(page, promptValue));
          message.success('Page scene saved');
          onUpdated?.();
        } catch (e) {
          setLocalError(e.message);
          message.error(e.message);
        } finally {
          setSaving(false);
        }
      }
      return;
    }
    setSaving(true);
    setLocalError(null);
    try {
      const scenes = (era.scenes || []).map((s) =>
        s.id === scene.id ? { ...s, prompt: promptValue, summary: s.summary || promptValue } : s
      );
      await updateChapter(projectId, era.id, { scenes });
      message.success('Page prompt saved');
      onUpdated?.();
    } catch (e) {
      setLocalError(e.message);
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleCastPrefix = (label) => {
    setCastPrefixes((prev) =>
      prev.includes(label) ? prev.filter((x) => x !== label) : [...prev, label]
    );
  };

  /** Self-contained — does not depend on parent prop drilling. */
  const runGenerate = async () => {
    if (busy || archived) return;
    setBusy(true);
    setLocalError(null);
    setLocalInfo(null);
    try {
      if (page?.id && promptValue !== pageSceneText(page, scene)) {
        await updatePage(projectId, page.id, pageScenePatch(page, promptValue));
      } else if (scene?.id) {
        const scenes = (era.scenes || []).map((s) =>
          s.id === scene.id ? { ...s, prompt: promptValue, summary: s.summary || promptValue } : s
        );
        await updateChapter(projectId, era.id, { scenes });
      }

      const data = await generateScene(projectId, {
        chapterId: era.id,
        pageId: page?.id,
        sceneId: scene?.id,
        override: override.trim() || undefined,
        castOverridePrefixes: castPrefixes.length ? castPrefixes : undefined,
        target: 'page',
        providerId: 'openai',
      });
      const generation = data.generation;
      if (generation?.error) {
        setLocalError(generation.error);
        setLocalInfo(null);
        message.error(generation.error);
      } else {
        setLocalGeneration(generation);
        setLocalInfo(
          `Generated via ${generation.api || 'images.generate'} — click the image to enlarge; Lock to keep it.`
        );
        message.success(`Generated “${pageTitle}”`);
        onGenerated?.(generation);
      }
    } catch (e) {
      const msg = await resolveGenerateError(projectId, e);
      setLocalError(msg);
      setLocalInfo(null);
      message.error(msg);
    } finally {
      setBusy(false);
    }
  };

  const pageIds = new Set(
    [page?.id, ...((scene?.pages || []).map((p) => p.id))].filter(Boolean)
  );
  const matchesPage = (g) =>
    !g?.error &&
    g?.output &&
    (hasPicture(g)) &&
    ((page?.id && g.pageId === page.id) ||
      (scene?.id && g.sceneId === scene.id) ||
      (g.pageId && pageIds.has(g.pageId)));

  const sceneGens = (generations || []).filter(matchesPage);
  const gallery = [...sceneGens];
  if (localGeneration && matchesPage(localGeneration) && !gallery.some((g) => g.id === localGeneration.id)) {
    gallery.unshift(localGeneration);
  }
  const shownError = displayGenerateError(
    localError,
    latestGenerateError(
      generations,
      (g) =>
        (page?.id && g.pageId === page.id) ||
        (scene?.id && g.sceneId === scene.id) ||
        (g.pageId && pageIds.has(g.pageId))
    )
  );

  return (
    <div className="era-scene-row" data-scene-id={scene?.id || page?.id} data-page-id={page?.id}>
      <div className="era-scene-row__head">
        <div>
          <Text strong style={{ fontSize: 'var(--text-body)' }}>
            {pageTitle}
            <NewPromptChip page={page} bibleVersion={bibleVersion} />
            <BibleMatchChip status={matchStatus} version={matchVersion} />
          </Text>
          {(scene?.pageNumbers || []).length > 0 && (
            <Tag style={{ marginInlineStart: 8 }}>p{(scene.pageNumbers || []).join('–')}</Tag>
          )}
          {page?.status && (
            <Tag style={{ marginInlineStart: 8 }} color={page.status === 'final' ? 'green' : 'default'}>
              {page.status}
            </Tag>
          )}
        </div>
      </div>

      <PageOtherRefs
        projectId={projectId}
        items={pageOthers}
        addable={addableOthers}
        generations={generations}
        disabled={archived || saving}
        onRemove={(itemId) => changePageOthers(itemId, 'remove')}
        onAdd={(itemId) => changePageOthers(itemId, 'add')}
      />

      <div className="era-scene-row__gallery">
        {gallery.length === 0 ? (
          <div className="era-scene-row__preview-empty">
            No generation yet — edit the page scene or add a note, then press Generate.
          </div>
        ) : (
          <>
            <Text type="secondary" className="era-scene-row__gallery-label">
              {gallery.length} attempt{gallery.length === 1 ? '' : 's'} · newest first · click to
              enlarge · Lock · Produce 300dpi, then Download
            </Text>
            <ul className="era-scene-gallery">
              {gallery.map((g) => (
                <li key={g.id} className="era-scene-gallery__item">
                  <GenerationThumb
                    projectId={projectId}
                    generation={g}
                    alt={`${pageTitle} · ${g.id}`}
                    caption={`${pageTitle} · ${g.providerId}/${g.model}${g.lockedAsReference ? ' · locked' : ''}${g.override ? ` · override: ${g.override}` : ''}`}
                    className="generation-thumb--gallery"
                  />
                  <GenerationAttemptActions
                    generation={g}
                    approvingId={approvingId}
                    unlockingId={unlockingId}
                    deletingId={deletingId}
                    exportingId={exportingId}
                    onApprove={onApprove}
                    onUnlock={onUnlockGeneration}
                    onDelete={
                      onDeleteGeneration
                        ? async (gen) => {
                            await onDeleteGeneration(gen);
                            if (localGeneration?.id === gen.id) setLocalGeneration(null);
                          }
                        : undefined
                    }
                    onExportGeneration={onExportGeneration}
                    showExport
                    onReuseOverride={
                      g.override || g.castOverridePrefixes?.length
                        ? () => {
                            if (g.override) setOverride(g.override);
                            if (g.castOverridePrefixes?.length) {
                              setCastPrefixes(g.castOverridePrefixes);
                            }
                          }
                        : undefined
                    }
                  />
                </li>
              ))}
            </ul>
          </>
        )}
      </div>

      <Button type="link" size="small" onClick={() => setPromptOpen((v) => !v)} style={{ paddingInline: 0 }}>
        {promptOpen ? 'Hide page scene' : 'Show page scene'}
      </Button>
      {promptOpen && (
        <>
          <Text type="secondary" style={{ display: 'block', fontSize: 'var(--text-caption)', marginBottom: 6 }}>
            Generate follows this. Write the placement you want drawn, such as which side of the van someone stands on.
          </Text>
          <Input.TextArea
            value={promptValue}
            onChange={(e) => setPromptValue(e.target.value)}
            disabled={archived || busy}
            rows={8}
            aria-label={`${pageTitle} page scene`}
            style={{ marginBottom: 8, fontFamily: 'var(--font-mono)', fontSize: 'var(--text-body-sm)' }}
          />
        </>
      )}

      {cast.length > 0 && !archived && (
        <div className="cast-override-chips" role="group" aria-label="Cast override prefixes">
          <Text type="secondary" style={{ fontSize: 'var(--text-caption)', display: 'block', marginBottom: 4 }}>
            Cast chips prefix the override (e.g. Dad: …)
          </Text>
          {cast.map((member) => {
            const label = member.label || member.role || member.characterId;
            if (!label) return null;
            const active = castPrefixes.includes(label);
            return (
              <Button
                key={member.characterId || label}
                size="small"
                type={active ? 'primary' : 'default'}
                className="pose-chip"
                aria-pressed={active}
                onClick={() => toggleCastPrefix(label)}
              >
                {label}
              </Button>
            );
          })}
        </div>
      )}

      <Input
        className="page-override-input"
        value={override}
        onChange={(e) => setOverride(e.target.value)}
        disabled={archived || busy}
        placeholder="Add for this attempt. The page scene stays"
        aria-label={`${pageTitle} generate override`}
        style={{ marginTop: 8 }}
      />

      <div className="era-scene-row__actions">
        {promptOpen && (
          <Button
            htmlType="button"
            type="default"
            className="era-control era-scene-row__save"
            loading={saving}
            disabled={archived || busy}
            onClick={saveTreatment}
          >
            Save scene
          </Button>
        )}
        <Button
          htmlType="button"
          type="primary"
          className="era-control era-scene-row__generate"
          icon={<ThunderboltOutlined />}
          loading={busy}
          disabled={archived}
          onClick={runGenerate}
          aria-label={`Generate illustration for ${pageTitle}`}
        >
          {busy ? 'Generating…' : 'Generate'}
        </Button>
      </div>
      {shownError && (
        <Alert type="error" showIcon style={{ marginTop: 10, whiteSpace: 'normal' }} title={shownError} />
      )}
      {!localError && localInfo && (
        <Alert type="success" showIcon style={{ marginTop: 10 }} title={localInfo} />
      )}
    </div>
  );
}

function EraCard({
  projectId,
  era,
  characters,
  others,
  bibleVersion,
  generations,
  onUpdated,
  onGenerated,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
  bibleMatch,
}) {
  const [titleValue, setTitleValue] = useState(era.title || '');
  const [summaryValue, setSummaryValue] = useState(era.summary || era.prompt || '');
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const archived = isArchivedItem(era);

  useEffect(() => {
    setTitleValue(era.title || '');
    setSummaryValue(era.summary || era.prompt || '');
  }, [era.id, era.title, era.summary, era.prompt]);

  const save = async () => {
    setSaving(true);
    try {
      await updateChapter(projectId, era.id, {
        title: titleValue.trim() || era.title,
        summary: summaryValue,
        prompt: summaryValue,
      });
      message.success('Chapter saved');
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteChapter(projectId, era.id);
      message.success('Chapter deleted');
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
      throw e;
    } finally {
      setBusy(false);
    }
  };

  const vehicle = era.vehicle;
  const refIds = era.referenceIds || [];

  const pageUnits = (() => {
    const pages = (era.pages || []).filter((p) => !isArchivedItem(p));
    if (pages.length) {
      return pages.map((page) => {
        const scene =
          (era.scenes || []).find((s) => s.id === page.sceneId) ||
          (era.scenes || []).find((s) => (s.pages || []).some((sp) => sp.id === page.id)) ||
          null;
        return { key: page.id, page, scene };
      });
    }
    return (era.scenes || []).map((scene) => ({
      key: scene.id,
      page: scene.pages?.[0] || null,
      scene,
    }));
  })();

  return (
    <section
      className={`era-card chapter-anchor${archived ? ' era-card--archived' : ''}`}
      id={`chapter-${era.id}`}
    >
      <div className="era-card__header">
        <div className="era-card__title-block">
          <Text type="secondary" className="era-card__meta">
            {era.year != null && (
              <Tag color="blue" style={{ marginInlineEnd: 8 }}>
                {era.year}
              </Tag>
            )}
            Chapter
            {era.eraNumber != null ? ` ${era.eraNumber}` : ''}
            {era.pageRange?.start != null &&
              ` · pages ${era.pageRange.start}–${era.pageRange.end}`}
            {archived ? ' · Archived' : ''}
            <BibleMatchChip status={bibleMatch?.chapters?.[era.id]} version={bibleMatch?.version} />
          </Text>
          <Input
            className="era-card__title-input"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            disabled={archived}
            aria-label="Chapter title"
          />
        </div>
        <div className="era-card__actions">
          <DeleteConfirmButton
            label="Delete"
            ariaLabel="Delete chapter"
            loading={busy}
            onConfirm={handleDelete}
          />
          <div className="era-card__actions-end">
            {archived ? (
              <Button
                htmlType="button"
                className="era-control"
                onClick={async () => {
                  await restoreChapter(projectId, era.id);
                  message.success('Chapter restored');
                  onUpdated?.();
                }}
              >
                Restore
              </Button>
            ) : (
              <Button
                htmlType="button"
                className="era-control"
                onClick={async () => {
                  await archiveChapter(projectId, era.id);
                  message.success('Chapter archived');
                  onUpdated?.();
                }}
              >
                Archive
              </Button>
            )}
            {!archived && (
              <Button
                htmlType="button"
                type="primary"
                className="era-control"
                loading={saving}
                onClick={save}
              >
                Save chapter
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="era-meta-block era-meta-block--scenes">
        <Text strong style={{ fontSize: 'var(--text-heading)' }}>
          Pages — Generate here
        </Text>
        <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 8 }}>
          Each page is a generation unit. Override posture/placement per attempt. Needs OpenAI key under{' '}
          <Link to="/admin">Admin</Link>.
        </Paragraph>
        {pageUnits.length === 0 ? (
          <Empty description="No pages in this chapter" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        ) : (
          <div className="era-scene-list">
            {pageUnits.map(({ key, page, scene }) => (
              <EraSceneRow
                key={key}
                projectId={projectId}
                era={era}
                scene={scene}
                page={page}
                characters={characters}
                others={others}
                bibleVersion={bibleVersion}
                archived={archived}
                generations={generations}
                onUpdated={onUpdated}
                onGenerated={onGenerated}
                onApprove={onApprove}
                approvingId={approvingId}
                onUnlockGeneration={onUnlockGeneration}
                unlockingId={unlockingId}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onExportGeneration={onExportGeneration}
                exportingId={exportingId}
                matchStatus={bibleMatch?.pages?.[page?.id]}
                matchVersion={bibleMatch?.version}
              />
            ))}
          </div>
        )}
      </div>

      <Text strong style={{ display: 'block', marginTop: 12 }}>
        Chapter treatment / continuity
      </Text>
      <Input.TextArea
        value={summaryValue}
        onChange={(e) => setSummaryValue(e.target.value)}
        disabled={archived}
        rows={3}
        style={{ marginTop: 6, marginBottom: 16 }}
        aria-label="Chapter treatment"
      />

      <EraCastChecklist cast={castStillInBook(era.cast, characters)} />

      {vehicle && (
        <div className="era-meta-block">
          <Text strong>Vehicle</Text>
          <Paragraph style={{ marginBottom: 0 }}>
            {vehicle.years} {vehicle.make} {vehicle.model}
            {vehicle.colors?.length ? ` · ${vehicle.colors.join('/')}` : ''}
            {vehicle.features?.length ? ` · ${vehicle.features.join(', ')}` : ''}
            {vehicle.note ? ` — ${vehicle.note}` : ''}
          </Paragraph>
        </div>
      )}

      {era.propsIntroduced?.length > 0 && (
        <div className="era-meta-block">
          <Text strong>Props introduced</Text>
          <div style={{ marginTop: 4 }}>
            {era.propsIntroduced.map((p) => (
              <Tag key={p}>{p}</Tag>
            ))}
          </div>
        </div>
      )}

      {era.tree && (
        <div className="era-meta-block">
          <Text strong>Tree</Text>
          <Paragraph style={{ marginBottom: 0 }}>
            Arrival: {era.tree.atArrival || 'n/a'}
            {era.tree.plantedPage != null ? ` · planted p${era.tree.plantedPage}` : ''}
            {era.tree.ageAtEra != null ? ` · age ${era.tree.ageAtEra}` : ''}
            {era.tree.note ? ` — ${era.tree.note}` : ''}
          </Paragraph>
        </div>
      )}

      {era.recurringMoment && (
        <div className="era-meta-block">
          <Text strong>Recurring moment</Text>
          <Paragraph style={{ marginBottom: 0 }}>{era.recurringMoment}</Paragraph>
        </div>
      )}

      {era.storyNotes?.length > 0 && (
        <div className="era-meta-block">
          <Text strong>Story text notes (not drawn)</Text>
          <ul className="era-note-list">
            {era.storyNotes.map((n) => (
              <li key={`${n.pages}-${n.text}`}>
                <Text strong>p{n.pages}:</Text> {n.text}
              </li>
            ))}
          </ul>
        </div>
      )}

      {era.mirrors?.length > 0 && (
        <div className="era-meta-block">
          <Text strong>Mirrors</Text>
          <ul className="era-note-list">
            {era.mirrors.map((m, i) => (
              <li key={i}>
                {typeof m === 'string' ? m : `${m.from} → ${(m.to || []).join(', ')}`}
              </li>
            ))}
          </ul>
        </div>
      )}

      {(refIds.length > 0 || characters?.some((c) => c.meta?.lockedSubject)) && (
        <div className="era-meta-block">
          <Text strong>Locked subjects / refs</Text>
          <div style={{ marginTop: 6 }}>
            {refIds.map((id) => (
              <Tag color="green" key={id}>
                {id}
              </Tag>
            ))}
            {(characters || [])
              .filter((c) => c.meta?.lockedSubject && c.meta?.eraYear === era.year)
              .map((c) => (
                <Tag key={c.id}>{c.title}</Tag>
              ))}
          </div>
        </div>
      )}
    </section>
  );
}

function ErasSection({
  projectId,
  eras,
  characters,
  others,
  bibleVersion,
  generations,
  onUpdated,
  onGenerated,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
  bibleMatch,
}) {
  const [adding, setAdding] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [yearValue, setYearValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const active = (eras || []).filter((e) => !isArchivedItem(e));
  const archived = (eras || []).filter((e) => isArchivedItem(e));
  const visible = showArchived ? archived : active;

  const createEra = async () => {
    if (!titleValue.trim()) {
      message.warning('Chapter title required');
      return;
    }
    setSaving(true);
    try {
      await addChapter(projectId, {
        kind: 'chapter',
        title: titleValue.trim(),
        year: yearValue ? Number(yearValue) : null,
        prompt: '',
        cast: [],
        scenes: [],
        pages: [],
      });
      message.success('Chapter added — add pages');
      setTitleValue('');
      setYearValue('');
      setAdding(false);
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="chapters-workspace" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="flex items-start justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <Paragraph style={{ color: 'var(--color-text-secondary)', marginBottom: 0, maxWidth: 'var(--measure-prose)' }}>
          Chapters → Pages (one image each). Year is metadata when present. Cast checklist keeps
          required subjects on every generate.
        </Paragraph>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {archived.length > 0 && (
            <Button type="link" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? 'Show active' : `Archived (${archived.length})`}
            </Button>
          )}
          {!showArchived && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
              Add chapter
            </Button>
          )}
        </div>
      </div>

      {visible.length > 1 && <ChapterJump chapters={visible} idPrefix="chapter-" />}

      {adding && !showArchived && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', maxWidth: 560 }}>
          <Input
            placeholder="1968 — The Beginning"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            autoFocus
            style={{ flex: 1, minWidth: 220 }}
          />
          <Input
            placeholder="Year"
            value={yearValue}
            onChange={(e) => setYearValue(e.target.value)}
            style={{ width: 96 }}
          />
          <Button type="primary" loading={saving} onClick={createEra}>
            Create
          </Button>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}

      {!visible.length ? (
        <Empty
          description={
            showArchived
              ? 'No archived chapters.'
              : 'No chapters yet — add one.'
          }
        />
      ) : (
        visible.map((era) => (
          <EraCard
            key={era.id}
            projectId={projectId}
            era={era}
            characters={characters}
            others={others}
            bibleVersion={bibleVersion}
            generations={generations}
            onUpdated={onUpdated}
            onGenerated={onGenerated}
            onApprove={onApprove}
            approvingId={approvingId}
            onUnlockGeneration={onUnlockGeneration}
            unlockingId={unlockingId}
            onDeleteGeneration={onDeleteGeneration}
            deletingId={deletingId}
            onExportGeneration={onExportGeneration}
            exportingId={exportingId}
            bibleMatch={bibleMatch}
          />
        ))
      )}
    </div>
  );
}

function ChaptersSection({ projectId, chapters, onChapterUpdated }) {
  const [adding, setAdding] = useState(false);
  const [titleValue, setTitleValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  const active = (chapters || []).filter((c) => !isArchivedItem(c));
  const archived = (chapters || []).filter((c) => isArchivedItem(c));
  const visible = showArchived ? archived : active;

  const createChapter = async () => {
    if (!titleValue.trim()) {
      message.warning('Chapter title required');
      return;
    }
    setSaving(true);
    try {
      await addChapter(projectId, { title: titleValue.trim(), prompt: '' });
      message.success('Chapter added');
      setTitleValue('');
      setAdding(false);
      onChapterUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="flex items-start justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <Paragraph style={{ color: 'var(--color-text-secondary)', marginBottom: 0, maxWidth: 'var(--measure-prose)' }}>
          Edit title + markdown treatment in place. Archive hides a chapter (and its pages) from the
          active list; delete permanently removes chapter + nested pages from disk.
        </Paragraph>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {archived.length > 0 && (
            <Button type="link" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? 'Show active' : `Archived (${archived.length})`}
            </Button>
          )}
          {!showArchived && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>
              Add chapter
            </Button>
          )}
        </div>
      </div>

      {adding && !showArchived && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', maxWidth: 480 }}>
          <Input
            placeholder="Chapter title"
            value={titleValue}
            onChange={(e) => setTitleValue(e.target.value)}
            autoFocus
          />
          <Button type="primary" loading={saving} onClick={createChapter}>
            Create
          </Button>
          <Button onClick={() => setAdding(false)}>Cancel</Button>
        </div>
      )}

      {!visible.length ? (
        <Empty
          description={
            showArchived
              ? 'No archived chapters.'
              : 'No chapters yet — add one, or scan Project Documents.'
          }
        />
      ) : (
        visible.map((chapter, index) => (
          <ChapterCard
            key={chapter.id}
            projectId={projectId}
            chapter={chapter}
            index={index}
            onUpdated={onChapterUpdated}
          />
        ))
      )}
    </div>
  );
}

function PageRow({
  projectId,
  chapter,
  page,
  workDpi,
  exportDpi,
  exportingId,
  onExport,
  onGenerate,
  generating,
  latestGeneration,
  onApprove,
  approvingId,
  onUpdated,
  showArchived,
}) {
  const [titleValue, setTitleValue] = useState(page.title || '');
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const archived = isArchivedItem(page);

  useEffect(() => {
    setTitleValue(page.title || '');
  }, [page.id, page.title]);

  if (showArchived !== archived) return null;

  const saveTitle = async () => {
    if (!titleValue.trim()) {
      message.warning('Page title required');
      return;
    }
    setBusy(true);
    try {
      await updatePage(projectId, page.id, { title: titleValue.trim() });
      message.success('Page saved');
      setEditing(false);
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setBusy(false);
    }
  };

  const imgSrc = generationImageSrc(projectId, latestGeneration);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
        padding: 'var(--space-4)',
        borderRadius: 'var(--radius-md)',
        border: '1px solid var(--color-border-subtle)',
        background: archived ? 'var(--color-bg-page)' : 'var(--color-bg-sunken)',
      }}
    >
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 'var(--space-3)',
        }}
      >
        <div style={{ flex: 1, minWidth: 200 }}>
          {editing ? (
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <Input
                value={titleValue}
                onChange={(e) => setTitleValue(e.target.value)}
                style={{ maxWidth: 280 }}
              />
              <Button type="primary" size="small" loading={busy} onClick={saveTitle}>
                Save
              </Button>
              <Button size="small" onClick={() => setEditing(false)}>
                Cancel
              </Button>
            </div>
          ) : (
            <>
              <Text strong>
                {page.title || page.id}
                {archived ? ' · archived' : ''}
                <NewPromptChip page={page} />
              </Text>
              <div style={{ marginTop: 4 }}>
                <Tag>Working · {page.workDpi || workDpi} dpi</Tag>
                <Tag>Export intent · {page.exportDpi || exportDpi} dpi</Tag>
                <Tag color={page.status === 'final' ? 'green' : 'default'}>{page.status || 'working'}</Tag>
                {latestGeneration?.lockedAsReference && <Tag>Locked</Tag>}
              </div>
            </>
          )}
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {!archived && !editing && (
            <>
              <Button
                size="small"
                type="primary"
                icon={<ThunderboltOutlined />}
                loading={generating}
                onClick={() => onGenerate?.(chapter, page)}
              >
                Generate
              </Button>
              <Button size="small" onClick={() => setEditing(true)}>
                Edit
              </Button>
              <Button
                size="small"
                icon={<ExportOutlined />}
                loading={exportingId === page.id}
                onClick={() => onExport(page)}
              >
                Export final @ {page.exportDpi || exportDpi} dpi
              </Button>
              <Button
                size="small"
                icon={<InboxOutlined />}
                loading={busy}
                onClick={async () => {
                  setBusy(true);
                  try {
                    await archivePage(projectId, page.id);
                    message.success('Page archived');
                    onUpdated?.();
                  } catch (e) {
                    message.error(e.message);
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                Archive
              </Button>
            </>
          )}
          {archived && (
            <Button
              size="small"
              icon={<InboxOutlined />}
              loading={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  await restorePage(projectId, page.id);
                  message.success('Page restored');
                  onUpdated?.();
                } catch (e) {
                  message.error(e.message);
                } finally {
                  setBusy(false);
                }
              }}
            >
              Restore
            </Button>
          )}
          <DeleteConfirmButton
            label="Delete"
            ariaLabel="Delete page"
            loading={busy}
            onConfirm={async () => {
              setBusy(true);
              try {
                await deletePage(projectId, page.id);
                message.success('Page deleted');
                onUpdated?.();
              } catch (e) {
                message.error(e.message);
                throw e;
              } finally {
                setBusy(false);
              }
            }}
          />
        </div>
      </div>

      {imgSrc && !latestGeneration?.error && (
        <div className="flex items-start gap-3" style={{ flexWrap: 'wrap' }}>
          <GenerationThumb
            projectId={projectId}
            generation={latestGeneration}
            alt={`Generation for ${page.title}`}
            caption={`${page.title} · ${latestGeneration.providerId}/${latestGeneration.model}`}
            className="generation-thumb--page"
          />
          <div style={{ maxWidth: 320 }}>
            <Text type="secondary" style={{ fontSize: 'var(--text-caption)', display: 'block' }}>
              Click to enlarge · {latestGeneration.providerId}/{latestGeneration.model} ·{' '}
              {latestGeneration.api || 'images.generate'} · work{' '}
              {latestGeneration.resolution?.workDpi || workDpi} dpi (export intent{' '}
              {latestGeneration.resolution?.exportDpi || exportDpi} dpi — not pixel-perfect)
            </Text>
            {!latestGeneration.lockedAsReference && (
              <Button
                size="small"
                type="default"
                style={{ marginTop: 8 }}
                loading={approvingId === latestGeneration.id}
                onClick={() => onApprove?.(latestGeneration)}
              >
                Lock
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PagesSection({
  projectId,
  chapters,
  resolution,
  generations,
  onGenerate,
  generating,
  onExport,
  exportingId,
  onApprove,
  approvingId,
  onUpdated,
}) {
  const workDpi = resolution?.workDpi || WORK_DPI;
  const exportDpi = resolution?.exportDpi || EXPORT_DPI;
  const activeChapters = (chapters || []).filter((c) => !isArchivedItem(c));
  const [pageChapterId, setPageChapterId] = useState(activeChapters[0]?.id || null);
  const [pageTitle, setPageTitle] = useState('');
  const [addingPage, setAddingPage] = useState(false);
  const [savingPage, setSavingPage] = useState(false);
  const [showArchived, setShowArchived] = useState(false);

  useEffect(() => {
    const firstActive = (chapters || []).find((c) => !isArchivedItem(c));
    if (!pageChapterId && firstActive?.id) setPageChapterId(firstActive.id);
  }, [chapters, pageChapterId]);

  if (!activeChapters.length && !showArchived) {
    return <Empty description="No active chapters — add or restore a chapter first, then add pages." />;
  }

  const createPage = async () => {
    if (!pageChapterId || !pageTitle.trim()) {
      message.warning('Choose a chapter and page title');
      return;
    }
    setSavingPage(true);
    try {
      await addPage(projectId, pageChapterId, { title: pageTitle.trim() });
      message.success('Page added');
      setPageTitle('');
      setAddingPage(false);
      onUpdated?.();
    } catch (e) {
      message.error(e.message);
    } finally {
      setSavingPage(false);
    }
  };

  const archivedPageCount = (chapters || []).reduce(
    (n, ch) => n + (ch.pages || []).filter((p) => isArchivedItem(p)).length,
    0
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <div className="flex items-start justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <Paragraph style={{ color: 'var(--color-text-secondary)', marginBottom: 0 }}>
          Pages live under chapters. Work at <Text strong>{workDpi} dpi</Text>; export final at{' '}
          <Text strong>{exportDpi} dpi</Text>. Same lifecycle: edit, archive, or delete with confirm.
        </Paragraph>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {archivedPageCount > 0 && (
            <Button type="link" onClick={() => setShowArchived((v) => !v)}>
              {showArchived ? 'Show active' : `Archived (${archivedPageCount})`}
            </Button>
          )}
          {!showArchived && (
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setAddingPage(true)}>
              Add page
            </Button>
          )}
        </div>
      </div>

      {addingPage && !showArchived && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <Select
            style={{ minWidth: 200 }}
            value={pageChapterId}
            onChange={setPageChapterId}
            options={activeChapters.map((c) => ({ value: c.id, label: c.title }))}
          />
          <Input
            placeholder="Page title"
            value={pageTitle}
            onChange={(e) => setPageTitle(e.target.value)}
            style={{ maxWidth: 240 }}
          />
          <Button type="primary" loading={savingPage} onClick={createPage}>
            Create
          </Button>
          <Button onClick={() => setAddingPage(false)}>Cancel</Button>
        </div>
      )}

      {activeChapters.map((chapter, index) => {
        const pages = (chapter.pages || []).filter((p) => isArchivedItem(p) === showArchived);
        if (showArchived && !pages.length) return null;
        return (
          <section
            key={chapter.id}
            style={{
              padding: 'var(--space-5)',
              background: 'var(--color-bg-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <div className="flex items-start justify-between gap-4" style={{ marginBottom: 12 }}>
              <div>
                <Text type="secondary" style={{ fontSize: 'var(--text-caption)' }}>
                  CHAPTER {index + 1}
                </Text>
                <Title level={3} style={{ fontFamily: 'var(--font-display)', margin: '4px 0 0' }}>
                  {chapter.title}
                </Title>
                {(chapter.prompt || chapter.summary) && (
                  <Paragraph type="secondary" ellipsis={{ rows: 2 }} style={{ marginBottom: 0, maxWidth: 520 }}>
                    Treatment: {chapter.prompt || chapter.summary}
                  </Paragraph>
                )}
              </div>
              {!showArchived && (
                <Button
                  type="primary"
                  icon={<ThunderboltOutlined />}
                  loading={generating}
                  onClick={() => onGenerate(chapter)}
                >
                  Generate from treatment
                </Button>
              )}
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
              {!pages.length && (
                <Text type="secondary">
                  {showArchived ? 'No archived pages in this chapter.' : 'No pages in this chapter yet.'}
                </Text>
              )}
              {(chapter.pages || []).map((page) => {
                const latestGeneration = (generations || []).find(
                  (g) => g.pageId === page.id && !g.error
                );
                return (
                  <PageRow
                    key={page.id}
                    projectId={projectId}
                    chapter={chapter}
                    page={page}
                    workDpi={workDpi}
                    exportDpi={exportDpi}
                    exportingId={exportingId}
                    onExport={onExport}
                    onGenerate={onGenerate}
                    generating={generating}
                    latestGeneration={latestGeneration}
                    onApprove={onApprove}
                    approvingId={approvingId}
                    onUpdated={onUpdated}
                    showArchived={showArchived}
                  />
                );
              })}
            </div>

            <div style={{ marginTop: 12 }}>
              <Link
                to={`/projects/${projectId}/chapters`}
                style={{ fontSize: 'var(--text-body-sm)', color: 'var(--color-text-secondary)' }}
              >
                Edit chapter treatment →
              </Link>
            </div>
          </section>
        );
      })}
    </div>
  );
}

function parseBibleFile(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      const name = (file.name || '').toLowerCase();
      if (name.endsWith('.json')) {
        try {
          resolve({ kind: 'json', bible: JSON.parse(text) });
        } catch (e) {
          reject(new Error(`Invalid JSON bible: ${e.message}`));
        }
        return;
      }
      resolve({ kind: 'markdown', markdown: text });
    };
    reader.onerror = () => reject(new Error('Could not read bible file'));
    reader.readAsText(file);
  });
}

function toDocumentFilename(file) {
  const name = file?.name || 'notes.md';
  // Canonical lowercase .md — same name always overwrites Storage + Firestore.
  if (name.toLowerCase().endsWith('.md')) return name.toLowerCase();
  if (name.toLowerCase().endsWith('.markdown')) {
    return `${name.slice(0, -'.markdown'.length).toLowerCase()}.md`;
  }
  return `${(name.replace(/\.[^.]+$/, '') || 'notes').toLowerCase()}.md`;
}

function treatmentSubtitle(era) {
  const summary = String(era?.summary || '').trim();
  const boilerplate = summary.match(/^Chapter (\d+) · \d{4}$/);
  if (!boilerplate) return summary;
  const number = era?.eraNumber != null ? era.eraNumber : boilerplate[1];
  const year = era?.year != null && era.year !== '' ? era.year : null;
  if (year == null) return summary;
  return `Chapter ${number} · ${year}`;
}

function treatmentImagesForEra(projectId, eraId, generations, target) {
  return (generations || [])
    .filter(
      (generation) =>
        generation?.target === target &&
        generation?.chapterId === eraId &&
        generationImageSrc(projectId, generation)
    )
    .sort((a, b) => {
      const aTime = a.createdAt ? Date.parse(a.createdAt) : NaN;
      const bTime = b.createdAt ? Date.parse(b.createdAt) : NaN;
      const aOk = Number.isFinite(aTime);
      const bOk = Number.isFinite(bTime);
      if (!aOk && !bOk) return 0;
      if (!aOk) return 1;
      if (!bOk) return -1;
      return bTime - aTime;
    });
}

function TreatmentPictureList({
  projectId,
  eraLabel,
  images,
  empty,
  approvingId,
  unlockingId,
  deletingId,
  onApprove,
  onUnlock,
  onDelete,
  onExportGeneration,
  exportingId,
}) {
  if (!images.length) {
    return <Text type="secondary">{empty}</Text>;
  }
  return (
    <ul className="era-scene-gallery">
      {images.map((generation) => (
        <li
          key={generation.id}
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            gap: 'var(--space-2)',
            flex: '0 1 280px',
            width: 280,
            maxWidth: '100%',
          }}
        >
          <GenerationThumb projectId={projectId} generation={generation} alt={`${eraLabel} treatment`} />
          <GenerationAttemptActions
            generation={generation}
            approvingId={approvingId}
            unlockingId={unlockingId}
            deletingId={deletingId}
            onApprove={onApprove}
            onUnlock={onUnlock}
            onDelete={onDelete}
            onExportGeneration={onExportGeneration}
            exportingId={exportingId}
          />
        </li>
      ))}
    </ul>
  );
}

/** One year: generate the treatment sheet from pictures already in the tool. */
function TreatmentCard({
  projectId,
  era,
  generations,
  onGenerated,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
  bibleMatch,
}) {
  const [action, setAction] = useState(null);

  const eraLabel = era.title || era.id;
  const yearText = era.year != null && era.year !== '' ? String(era.year) : '';
  const titleHasYear = Boolean(yearText && eraLabel.startsWith(yearText));
  const subtitle = treatmentSubtitle(era);
  const sheets = treatmentImagesForEra(projectId, era.id, generations, 'treatment-sheet');

  const run = async (name, fn, ok) => {
    setAction(name);
    try {
      await fn();
      if (ok) message.success(ok);
    } catch (e) {
      message.error(e.message || 'Something went wrong');
    } finally {
      setAction(null);
    }
  };

  const doGenerateSheet = () =>
    run('sheet', async () => {
      const res = await generateTreatmentSheet(projectId, era.id);
      if (res.generation) onGenerated?.(res.generation);
    }, 'Treatment generated');

  return (
    <section
      id={`treatment-${era.id}`}
      className="treatment-card"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-4)',
        padding: 'var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--color-border-subtle)',
        background: 'var(--color-bg-sunken)',
        boxShadow: 'var(--shadow-sm)',
      }}
    >
      <div className="flex items-start justify-between gap-3" style={{ flexWrap: 'wrap' }}>
        <div>
          <Title
            level={3}
            style={{
              margin: 0,
              fontFamily: 'var(--font-display)',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'baseline',
              columnGap: '0.5rem',
            }}
          >
            <span>{eraLabel}</span>
            {yearText && !titleHasYear ? <Text type="secondary">{yearText}</Text> : null}
            <BibleMatchChip status={bibleMatch?.chapters?.[era.id]} version={bibleMatch?.version} />
          </Title>
          {subtitle ? (
            <Paragraph type="secondary" style={{ marginTop: 4, marginBottom: 0, maxWidth: 'var(--measure-prose)' }}>
              {subtitle}
            </Paragraph>
          ) : null}
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div>
          <Button
            type="primary"
            icon={<ThunderboltOutlined />}
            loading={action === 'sheet'}
            onClick={doGenerateSheet}
          >
            Generate treatment
          </Button>
        </div>
        <TreatmentPictureList
          projectId={projectId}
          eraLabel={`${eraLabel} treatment`}
          images={sheets}
          empty="No treatment yet"
          approvingId={approvingId}
          unlockingId={unlockingId}
          deletingId={deletingId}
          onApprove={onApprove}
          onUnlock={onUnlockGeneration}
          onDelete={onDeleteGeneration}
          onExportGeneration={onExportGeneration}
          exportingId={exportingId}
        />
      </div>
    </section>
  );
}

function TreatmentSection({
  projectId,
  eras,
  generations,
  onGenerated,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
  bibleMatch,
}) {
  const active = (eras || []).filter((era) => !isArchivedItem(era));
  return (
    <div className="treatment-workspace" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      <Paragraph style={{ color: 'var(--color-text-secondary)', marginBottom: 0, maxWidth: 'var(--measure-prose)' }}>
        Each year has one treatment. Generate treatment draws it from the pictures already saved:
        notes beside the characters, one note on the van in the main scene, a few scenes from the story,
        and 5 to 7 other items along the bottom.
      </Paragraph>
      {!active.length ? (
        <Empty description="No chapters yet — add eras under Chapters first." />
      ) : (
        active.map((era) => (
          <TreatmentCard
            key={era.id}
            projectId={projectId}
            era={era}
            generations={generations}
            onGenerated={onGenerated}
            onApprove={onApprove}
            approvingId={approvingId}
            onUnlockGeneration={onUnlockGeneration}
            unlockingId={unlockingId}
            onDeleteGeneration={onDeleteGeneration}
            deletingId={deletingId}
            onExportGeneration={onExportGeneration}
            exportingId={exportingId}
            bibleMatch={bibleMatch}
          />
        ))
      )}
    </div>
  );
}

function isCurrentBibleFile(name, version) {
  const file = String(name || '').toLowerCase();
  const current = String(version || '').trim().toLowerCase();
  if (!file || !current) return false;
  return file.includes(`v${current}`) || file.includes(`_${current}_`) || file.includes(`-${current}-`);
}

function DocumentsSection({
  projectId,
  bibleVersion,
  generations,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
}) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listDocuments(projectId);
      setDocuments(data.documents || []);
      if (data.source === 'local' && data.warning) {
        setError(
          `Documents are on local disk only (not durable): ${data.warning}. Sign in so uploads go to Firebase Storage.`
        );
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const onDownload = async (doc) => {
    try {
      const data = await getDocument(projectId, doc.id);
      const blob = new Blob([data.document?.markdown || ''], {
        type: 'text/markdown;charset=utf-8',
      });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = doc.name || 'bible.md';
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      message.error(e.message || 'Could not download that file');
    }
  };

  const onRemove = async (doc) => {
    try {
      await deleteDocument(projectId, doc.id);
      message.success('Document removed');
      await refresh();
    } catch (e) {
      message.error(e.message || 'Could not remove document');
      // Do not rethrow — Popconfirm would surface CRA's "Uncaught runtime errors" overlay.
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-6)' }}>
      {error && <Alert type="error" showIcon title={error} />}

      {loading ? (
        <div className="watercolor-loader-slot">
          <WatercolorLoader />
        </div>
      ) : !documents.length ? (
        <Empty description="No files yet. Upload a bible in Settings." />
      ) : (
        <List
          dataSource={documents}
          renderItem={(doc) => (
            <List.Item
              actions={[
                <Button key="download" type="link" onClick={() => onDownload(doc)}>
                  Download
                </Button>,
                <DeleteConfirmButton
                  key="remove"
                  type="link"
                  label="Remove"
                  action="remove"
                  onConfirm={() => onRemove(doc)}
                />,
              ]}
            >
              <List.Item.Meta
                avatar={<FileTextOutlined style={{ fontSize: 20, color: 'var(--color-accent-text)' }} />}
                title={
                  <span>
                    {doc.name}{' '}
                    {isCurrentBibleFile(doc.name, bibleVersion) ? <Tag>Current</Tag> : null}
                  </span>
                }
                description={`${doc.size} bytes · updated ${new Date(doc.updatedAt).toLocaleString()}`}
              />
            </List.Item>
          )}
        />
      )}

      <ImageGallery>
        <GenerationsFooter
          generations={generations}
          projectId={projectId}
          onApprove={onApprove}
          approvingId={approvingId}
          onUnlockGeneration={onUnlockGeneration}
          unlockingId={unlockingId}
          onDeleteGeneration={onDeleteGeneration}
          deletingId={deletingId}
          onExportGeneration={onExportGeneration}
          exportingId={exportingId}
        />
      </ImageGallery>
    </div>
  );
}

function SettingsSection({ project, bible, onUpdated }) {
  const navigate = useNavigate();
  const [description, setDescription] = useState(project.description || '');
  const [savingDesc, setSavingDesc] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [biblePreview, setBiblePreview] = useState(null);
  const [bibleFile, setBibleFile] = useState(null);
  const [castLock, setCastLock] = useState(project.chapterCastLock !== false);
  const [savingCastLock, setSavingCastLock] = useState(false);

  useEffect(() => {
    setDescription(project.description || '');
    setCastLock(project.chapterCastLock !== false);
  }, [project.description, project.id, project.chapterCastLock]);

  const onCastLock = async (checked) => {
    setCastLock(checked);
    setSavingCastLock(true);
    try {
      const { project: next } = await updateProject(project.id, { chapterCastLock: checked });
      message.success(checked ? 'Chapters keep their own characters' : 'Chapters can share characters');
      onUpdated?.(next);
    } catch (e) {
      setCastLock(project.chapterCastLock !== false);
      message.error(e.message);
    } finally {
      setSavingCastLock(false);
    }
  };

  const saveDescription = async () => {
    setSavingDesc(true);
    try {
      const { project: next } = await updateProject(project.id, { description });
      message.success('Description saved');
      onUpdated?.(next);
      window.dispatchEvent(new Event('artificiallly:projects-changed'));
    } catch (e) {
      message.error(e.message);
    } finally {
      setSavingDesc(false);
    }
  };

  const onBibleFile = async (file) => {
    try {
      const parsed = await parseBibleFile(file);
      setBibleFile(file);
      setBiblePreview(parsed);
    } catch (e) {
      setBibleFile(null);
      setBiblePreview(null);
      message.error(e.message);
    }
    return false;
  };

  const reportBibleUpload = (result) => {
    const label = (item) => {
      const name = item.title || item.id;
      if (item.added) return `${name} (added)`;
      if (item.archived) return `${name} (archived)`;
      return name;
    };
    const characters = result?.promptsUpdated || [];
    const groups = [
      result?.animationStillsUpdated
        ? { label: 'Van pictures', items: ['Road picture, top down, and motion'] }
        : null,
      { label: 'Van animation', items: characters.filter((item) => item.animation).map(label) },
      {
        label: 'Characters',
        items: characters.filter((item) => !item.animation || item.prompt).map(label),
      },
      {
        label: 'Mannerisms',
        items: characters
          .filter((item) => item.mannerism)
          .map((item) => {
            const cells = (item.mannerismCells || []).join(', ');
            return cells ? `${label(item)} — ${cells}` : label(item);
          }),
      },
      { label: 'Other', items: (result?.othersUpdated || []).map(label) },
      { label: 'Pages', items: (result?.pagesUpdated || []).map(label) },
    ].filter((group) => group && group.items.length);
    if (!groups.length) {
      message.success('Bible uploaded. Prompts already match. Pictures stay.');
      return;
    }
    Modal.success({
      title: 'Bible uploaded. Pictures stay.',
      width: 560,
      content: (
        <div style={{ maxHeight: '60vh', overflow: 'auto', paddingRight: 8 }}>
          {groups.map((group) => (
            <div key={group.label} style={{ marginTop: 12 }}>
              <p style={{ margin: '0 0 4px', fontWeight: 600 }}>
                {group.label} ({group.items.length})
              </p>
              <ul style={{ margin: 0, paddingInlineStart: 18 }}>
                {group.items.map((name, index) => (
                  <li key={`${group.label}-${index}`}>{name}</li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      ),
    });
  };

  const uploadBible = async () => {
    if (!biblePreview) {
      message.warning('Choose an Illustration Bible file first');
      return;
    }
    setUploading(true);
    try {
      const payload =
        biblePreview.kind === 'json'
          ? biblePreview.bible
          : {
              markdown: biblePreview.markdown,
              version: project.bibleVersion || '1.0',
              title: `${project.title} — Illustration Bible`,
            };
      const result = await ingestBible(project.id, payload);
      if (biblePreview.kind !== 'json' && biblePreview.markdown && bibleFile?.name) {
        try {
          await uploadDocument(project.id, {
            name: toDocumentFilename(bibleFile),
            markdown: biblePreview.markdown,
          });
        } catch (e) {
          message.warning(`The book updated, but the history file was not saved. ${e.message}`);
        }
      }
      reportBibleUpload(result);
      setBibleFile(null);
      setBiblePreview(null);
      onUpdated?.();
      window.dispatchEvent(new Event('artificiallly:projects-changed'));
    } catch (e) {
      message.error(e.message);
    } finally {
      setUploading(false);
    }
  };

  const onArchive = () => {
    Modal.confirm({
      title: `Archive “${project.title}”?`,
      content:
        'The project leaves the active list. You can restore or delete it fully from Archived projects.',
      okText: 'Archive project',
      onOk: async () => {
        setArchiving(true);
        try {
          await archiveProject(project.id);
          message.success('Project archived');
          window.dispatchEvent(new Event('artificiallly:projects-changed'));
          navigate('/archived');
        } catch (e) {
          message.error(e.message);
          throw e;
        } finally {
          setArchiving(false);
        }
      },
    });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-8)', maxWidth: 560 }}>
      <section>
        <Title level={3} style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>
          Book size
        </Title>
        <Paragraph type="secondary" style={{ marginBottom: 8 }}>
          Trim from project documents (<Text code>Book size:</Text> / frontmatter{' '}
          <Text code>book_size</Text>). Per-page image sizes can differ via{' '}
          <Text code>Image size:</Text> on scene treatments or page prompts.
        </Paragraph>
        {project.bookSize ? (
          <Text>
            {project.bookSize.label ||
              `${project.bookSize.widthIn} × ${project.bookSize.heightIn} in`}
            {project.bookSize.providerSize ? ` · default gen ${project.bookSize.providerSize}` : ''}
          </Text>
        ) : (
          <Text type="secondary">Not set yet — upload or Scan documents that declare Book size.</Text>
        )}
      </section>

      <section>
        <Title level={3} style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>
          Characters
        </Title>
        <div style={{ display: 'flex', gap: 'var(--space-3)', alignItems: 'flex-start' }}>
          <Switch
            checked={castLock}
            loading={savingCastLock}
            onChange={onCastLock}
            aria-label="Keep characters in their chapter"
          />
          <div>
            <Text strong style={{ display: 'block' }}>
              Keep characters in their chapter
            </Text>
            <Paragraph type="secondary" style={{ marginBottom: 0 }}>
              A chapter uses only the characters locked to it. Turn this off for a coloring book.
            </Paragraph>
          </div>
        </div>
      </section>

      <section>
        <Title level={3} style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>
          Description
        </Title>
        <Paragraph type="secondary">Add a short description for this book project.</Paragraph>
        <Input.TextArea
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="What is this book about?"
        />
        <Button
          type="primary"
          onClick={saveDescription}
          loading={savingDesc}
          style={{ marginTop: 12 }}
        >
          Save description
        </Button>
      </section>

      <section>
        <Title level={3} style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>
          Download changes
        </Title>
        <Paragraph type="secondary">
          Download a markdown file of character, other, and page prompts that differ from the
          Illustration Bible.
        </Paragraph>
        <Button
          loading={exporting}
          onClick={async () => {
            setExporting(true);
            try {
              const result = await exportBibleChanges(project.id);
              const total = result?.counts?.total || 0;
              if (!total) {
                message.info('No prompt changes.');
                return;
              }
              const blob = new Blob([result.markdown || ''], {
                type: 'text/markdown;charset=utf-8',
              });
              const url = URL.createObjectURL(blob);
              const link = document.createElement('a');
              link.href = url;
              link.download = result.filename || `${project.slug || project.id}-bible-changes.md`;
              document.body.appendChild(link);
              link.click();
              link.remove();
              URL.revokeObjectURL(url);
              message.success(`Downloaded ${total} prompt change${total === 1 ? '' : 's'}`);
            } catch (e) {
              message.error(e.message);
            } finally {
              setExporting(false);
            }
          }}
        >
          Download changes
        </Button>
      </section>

      <section>
        <Title level={3} style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>
          Upload new bible
        </Title>
        <Paragraph type="secondary">
          Upload an updated Illustration Bible (JSON or markdown). Generated pictures stay on their pages. Current: v{project.bibleVersion}
          {bible?.status ? ` · ${bible.status}` : ''}.
        </Paragraph>
        <Dragger
          accept=".json,.md,.markdown,.txt"
          multiple={false}
          maxCount={1}
          beforeUpload={onBibleFile}
          onRemove={() => {
            setBibleFile(null);
            setBiblePreview(null);
          }}
          fileList={
            bibleFile
              ? [{ uid: 'bible', name: bibleFile.name, status: 'done' }]
              : []
          }
        >
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">Drop Illustration Bible here</p>
        </Dragger>
        <Button
          type="primary"
          onClick={uploadBible}
          loading={uploading}
          disabled={!biblePreview}
          style={{ marginTop: 12 }}
        >
          Upload bible
        </Button>
      </section>

      <section>
        <Title level={3} style={{ fontFamily: 'var(--font-display)', marginBottom: 8 }}>
          Archive project
        </Title>
        <Paragraph type="secondary">
          Archive removes it from the active Project List. Delete fully only from Archived projects.
        </Paragraph>
        <Button danger loading={archiving} onClick={onArchive}>
          Archive project
        </Button>
      </section>
    </div>
  );
}

function GenerationsFooter({
  generations,
  projectId,
  onApprove,
  approvingId,
  onUnlockGeneration,
  unlockingId,
  onDeleteGeneration,
  deletingId,
  onExportGeneration,
  exportingId,
}) {
  if (!generations?.length) return null;
  return (
    <div style={{ marginTop: 'var(--space-10)' }}>
      <Title level={4} style={{ fontFamily: 'var(--font-display)' }}>
        Recent generations
      </Title>
      <Paragraph type="secondary" style={{ fontSize: 'var(--text-caption)' }}>
        Stored locally under generations/ and synced to Firebase Storage + Firestore when signed in.
        Lock keeps an image as a reference for continuity (images.edit on next runs).
        Unlock before delete.
      </Paragraph>
      <List
        size="small"
        dataSource={generations.slice(0, 8)}
        renderItem={(g) => {
          const locked = !!(g.lockedAsReference || g.approved);
          return (
          <List.Item
            actions={
              !g.error && hasPicture(g)
                ? [
                    locked ? (
                      <span key="locked" className="era-scene-gallery__locked">
                        <Popconfirm
                          title="Are you sure?"
                          okText="Yes, unlock"
                          cancelText="No, cancel"
                          okButtonProps={{ size: 'small' }}
                          cancelButtonProps={{ size: 'small' }}
                          placement="top"
                          rootClassName="confirm-popconfirm"
                          onConfirm={() => onUnlockGeneration?.(g)}
                          disabled={!onUnlockGeneration}
                        >
                          <Button
                            htmlType="button"
                            type="text"
                            size="small"
                            className="era-control era-scene-gallery__lock"
                            icon={<LockOutlined />}
                            loading={unlockingId === g.id}
                            disabled={!onUnlockGeneration}
                            aria-label="Unlock this picture"
                          />
                        </Popconfirm>
                        <Tag className="era-scene-gallery__lock-chip">Locked</Tag>
                        {onExportGeneration ? (
                          <PrintExportButton
                            generation={g}
                            exportingId={exportingId}
                            onExportGeneration={onExportGeneration}
                          />
                        ) : null}
                      </span>
                    ) : (
                      <Button
                        key="approve"
                        type="link"
                        loading={approvingId === g.id}
                        onClick={() => onApprove?.(g)}
                      >
                        Lock
                      </Button>
                    ),
                    !locked && onDeleteGeneration ? (
                      <DeleteConfirmButton
                        key="delete"
                        label="Delete"
                        size="small"
                        type="link"
                        loading={deletingId === g.id}
                        ariaLabel={`Delete generation ${g.id}`}
                        onConfirm={() => onDeleteGeneration(g)}
                      />
                    ) : null,
                  ].filter(Boolean)
                : undefined
            }
          >
            <List.Item.Meta
              title={
                <span>
                  {g.pageId || g.chapterId || g.sceneId || g.id} · {g.providerId}/{g.model}{' '}
                  <Tag>{g.api || 'images.generate'}</Tag>
                  {g.error ? <Tag color="red">error</Tag> : <Tag color="green">ok</Tag>}
                </span>
              }
              description={
                hasPicture(g) ? (
                  <GenerationThumb
                    projectId={projectId}
                    generation={g}
                    alt={`Generation ${g.id}`}
                    caption={`${g.sceneId || g.pageId || g.chapterId || g.id} · ${g.providerId}/${g.model}${g.lockedAsReference ? ' · locked' : ''}`}
                    className="generation-thumb--footer"
                  />
                ) : (
                  <Text type="secondary" style={{ fontSize: 12 }}>
                    {g.error || g.createdAt}
                  </Text>
                )
              }
            />
          </List.Item>
          );
        }}
      />
    </div>
  );
}

const ProjectPage = () => {
  const { projectId } = useParams();
  const [project, setProject] = useState(null);
  const [bible, setBible] = useState(null);
  const [structure, setStructure] = useState(null);
  const [bibleMatch, setBibleMatch] = useState(null);
  const [generations, setGenerations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exportingId, setExportingId] = useState(null);
  const [approvingId, setApprovingId] = useState(null);
  const [unlockingId, setUnlockingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [error, setError] = useState(null);
  const [info, setInfo] = useState(null);

  const refresh = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setError(null);
    try {
      const data = await getProject(projectId);
      setProject(data.project);
      setBible(data.bible);
      setStructure(data.structure || data.project?.structure || null);
      setBibleMatch(data.bibleMatch || null);
      try {
        const gens = await listGenerations(projectId);
        setGenerations(gens.generations || []);
      } catch (genErr) {
        setError(genErr.message);
      }

      const bibleSnapshot = data.bible;
      if (shouldMirrorProjectToCloud()) void (async () => {
        try {
          const approved = (bibleSnapshot?.references || []).filter((r) => r.approved && r.path);
          for (const reference of approved) {
            await syncLocalApprovedReference(projectId, reference);
          }
        } catch {
          /* offline / unsigned — local disk remains source of truth */
        }
      })();
    } catch (e) {
      setError(e.message);
    } finally {
      if (!silent) setLoading(false);
    }
  }, [projectId]);

  const refreshSection = useCallback(() => refresh({ silent: true }), [refresh]);

  const applyProjectMutation = useCallback((data) => {
    const nextProject = data?.project || null;
    if (nextProject) {
      setProject(nextProject);
      if (nextProject.structure) setStructure(nextProject.structure);
    } else if (data?.structure) {
      setStructure(data.structure);
    }
  }, []);

  const upsertGeneration = useCallback((generation) => {
    if (!generation?.id) return;
    setGenerations((prev) => {
      const previous = (prev || []).find((g) => g.id === generation.id);
      const next = keepPicture(previous, generation);
      const rest = (prev || []).filter((g) => g.id !== generation.id);
      return [next, ...rest];
    });
    if (!writesStayLocal()) {
      attachGenerationPicture(projectId, generation)
        .then((withPicture) => {
          setGenerations((prev) =>
            (prev || []).map((g) => (g.id === withPicture.id ? keepPicture(g, withPicture) : g))
          );
        })
        .catch(() => {});
    }
  }, [projectId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // organizationModel may still be "eras" in project.json — UI always uses Chapters.
  const chapters = structure?.chapters || [];
  const eras =
    structure?.eras?.length
      ? structure.eras
      : chapters.filter((c) => c.kind === 'era' || c.kind === 'chapter' || c.year != null);
  const chapterUnits = (() => {
    const fromChapters = chapters.filter(
      (c) => (c.pages || []).length > 0 || (c.scenes || []).length > 0 || c.kind === 'era' || c.year != null
    );
    if (fromChapters.length) return fromChapters;
    if (eras.length) return eras;
    return chapters;
  })();
  const activeChapterCount = chapterUnits.filter((c) => !isArchivedItem(c)).length;
  const illustrations = (generations || []).filter(
    (g) => !g.error && hasPicture(g)
  );
  const lockedIllustrationCount = illustrations.filter(
    (g) => g.lockedAsReference || g.approved
  ).length;
  const defaultSection = 'chapters';

  const onApprove = async (generation) => {
    if (!generation?.id) return;
    setApprovingId(generation.id);
    try {
      const result = await approveGeneration(projectId, generation.id, {
        caption:
          generation.pageId ||
          generation.characterId ||
          generation.otherId ||
          generation.chapterId ||
          generation.id,
      });
      message.success(
        `Locked as reference ${result.reference?.id}` +
          (result.cloudSynced ? ' · Firebase' : '')
      );
      if (result.generation) {
        setGenerations((prev) => {
          const next = keepPicture(
            (prev || []).find((g) => g.id === result.generation.id),
            result.generation
          );
          const rest = (prev || []).filter((g) => g.id !== result.generation.id);
          return [next, ...rest];
        });
      }
      await refreshSection();
    } catch (e) {
      message.error(e.message);
    } finally {
      setApprovingId(null);
    }
  };

  const onUnlockGeneration = async (generation) => {
    if (!generation?.id) return;
    setUnlockingId(generation.id);
    setError(null);
    try {
      const result = await unlockGeneration(projectId, generation.id, {
        approvedReferenceId: generation.approvedReferenceId || null,
      });
      message.success(`Unlocked ${generation.id.slice(0, 8)}…`);
      if (result.generation) {
        setGenerations((prev) =>
          prev.map((g) => (g.id === result.generation.id ? keepPicture(g, result.generation) : g))
        );
      }
      await refreshSection();
    } catch (e) {
      message.error(e.message || 'Could not unlock generation');
    } finally {
      setUnlockingId(null);
    }
  };

  const onDeleteGeneration = async (generation) => {
    if (!generation?.id) return;
    if (generation.lockedAsReference || generation.approved) {
      message.warning('Unlock this generation before deleting it');
      return;
    }
    setDeletingId(generation.id);
    setError(null);
    try {
      await deleteGeneration(projectId, generation.id, {
        approvedReferenceId: generation.approvedReferenceId || null,
      });
      message.success(`Deleted generation ${generation.id.slice(0, 8)}…`);
      setGenerations((prev) => prev.filter((g) => g.id !== generation.id));
      await refreshSection();
    } catch (e) {
      message.error(e.message || 'Could not delete generation');
    } finally {
      setDeletingId(null);
    }
  };

  const onExportGeneration = async (generation, downloadOnly) => {
    if (!generation?.id) return;
    setExportingId(generation.id);
    setError(null);
    try {
      const existing = generation.export300;
      if (downloadOnly && existing?.path) {
        const filename = String(existing.path).split('/').pop();
        await downloadExportFile(projectId, existing, filename);
        message.success('Saved to Downloads');
        setInfo('Saved the 300dpi PNG to Downloads');
        return;
      }
      const result = await exportGeneration(projectId, generation.id);
      const file = result?.export;
      if (result?.generation) upsertGeneration(result.generation);
      if (!file?.path) throw new Error('300dpi file was not saved');
      message.success(
        result.reused
          ? '300dpi is already ready. Download saves the file.'
          : '300dpi is ready. Download saves the file. No new picture was made.'
      );
      setInfo('300dpi is ready');
      await refreshSection();
    } catch (e) {
      message.error(e.message || 'Could not save the 300dpi PNG');
      setError(e.message);
    } finally {
      setExportingId(null);
    }
  };

  if (loading) {
    return (
      <div className="workflow-shell__content watercolor-loader-slot watercolor-loader-slot--page">
        <WatercolorLoader size="large" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="workflow-shell__content">
        <Alert type="error" title={error || 'Project not found'} />
        <Link to="/">Back to studio</Link>
      </div>
    );
  }

  return (
    <div className="workflow-shell__content">
      <Title
        level={1}
        style={{
          fontFamily: 'var(--font-display)',
          fontSize: 'var(--text-title)',
          letterSpacing: 'var(--tracking-title)',
          marginBottom: 4,
        }}
      >
        {project.title}
      </Title>
      <Paragraph type="secondary" style={{ marginBottom: 'var(--space-4)' }}>
        {activeChapterCount} chapter{activeChapterCount === 1 ? '' : 's'} · Bible v{project.bibleVersion}
        {' · '}
        {illustrations.length} illustration{illustrations.length === 1 ? '' : 's'}
        {' · '}
        {lockedIllustrationCount} locked{' '}
        {bible?.status === 'placeholder' ? (
          <Tag color="orange">bible placeholder</Tag>
        ) : (
          <Tag color="green">{bible?.status || 'ready'}</Tag>
        )}
      </Paragraph>

      <ProjectSectionNav projectId={projectId} project={project} />

      {error && <Alert type="error" showIcon title={error} style={{ marginBottom: 12 }} />}
      {info && <Alert type="info" showIcon title={info} style={{ marginBottom: 12 }} />}

      <Routes>
        <Route index element={<Navigate to={defaultSection} replace />} />
        <Route
          path="characters"
          element={
            <ImageGallery>
            <CharactersSection
              projectId={projectId}
              items={structure?.characters}
              chapters={chapterUnits}
              generations={generations}
              expressionCatalog={structure?.expressionCatalog}
              onStructureChange={applyProjectMutation}
              onAdd={async (body) => {
                applyProjectMutation(await addCharacter(projectId, body));
              }}
              onUpdate={async (id, body) => {
                applyProjectMutation(await updateCharacter(projectId, id, body));
              }}
              onArchive={async (id) => {
                applyProjectMutation(await archiveCharacter(projectId, id));
              }}
              onRestore={async (id) => {
                applyProjectMutation(await restoreCharacter(projectId, id));
              }}
              onDelete={async (id) => {
                applyProjectMutation(await deleteCharacter(projectId, id));
              }}
              onApprove={onApprove}
              approvingId={approvingId}
              onUnlockGeneration={onUnlockGeneration}
              unlockingId={unlockingId}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
              onExportGeneration={onExportGeneration}
              exportingId={exportingId}
              onGenerated={upsertGeneration}
              onUpdated={refreshSection}
              onUploadBaseReference={async (id, body) => {
                applyProjectMutation(await uploadCharacterBaseReference(projectId, id, body));
              }}
              onClearBaseReference={async (id) => {
                applyProjectMutation(await clearCharacterBaseReference(projectId, id));
              }}
              bibleMatch={bibleMatch}
            />
            </ImageGallery>
          }
        />
        <Route
          path="other"
          element={
            <ImageGallery>
            <OtherSection
              projectId={projectId}
              items={structure?.other}
              chapters={chapterUnits}
              generations={generations}
              onAdd={async (body) => {
                applyProjectMutation(await addOther(projectId, body));
              }}
              onUpdate={async (id, body) => {
                applyProjectMutation(await updateOther(projectId, id, body));
              }}
              onArchive={async (id) => {
                applyProjectMutation(await archiveOther(projectId, id));
              }}
              onRestore={async (id) => {
                applyProjectMutation(await restoreOther(projectId, id));
              }}
              onDelete={async (id) => {
                applyProjectMutation(await deleteOther(projectId, id));
              }}
              onApprove={onApprove}
              approvingId={approvingId}
              onUnlockGeneration={onUnlockGeneration}
              unlockingId={unlockingId}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
              onExportGeneration={onExportGeneration}
              exportingId={exportingId}
              onGenerated={upsertGeneration}
              onUpdated={refreshSection}
              onUploadBaseReference={async (id, body) => {
                applyProjectMutation(await uploadOtherBaseReference(projectId, id, body));
              }}
              onClearBaseReference={async (id) => {
                applyProjectMutation(await clearOtherBaseReference(projectId, id));
              }}
              bibleMatch={bibleMatch}
            />
            </ImageGallery>
          }
        />
        <Route path="eras" element={<Navigate to="../chapters" replace />} />
        <Route
          path="chapters"
          element={
            <ImageGallery>
            <ErasSection
              projectId={projectId}
              eras={chapterUnits}
              characters={structure?.characters}
              others={structure?.other}
              bibleVersion={project.bibleVersion}
              generations={generations}
              onUpdated={refreshSection}
              onGenerated={upsertGeneration}
              onApprove={onApprove}
              approvingId={approvingId}
              onUnlockGeneration={onUnlockGeneration}
              unlockingId={unlockingId}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
              onExportGeneration={onExportGeneration}
              exportingId={exportingId}
              bibleMatch={bibleMatch}
            />
            </ImageGallery>
          }
        />
        <Route path="pages" element={<Navigate to="../chapters" replace />} />
        <Route
          path="treatments"
          element={
            <ImageGallery>
              <TreatmentSection
                projectId={projectId}
                eras={chapterUnits}
                generations={generations}
                onGenerated={upsertGeneration}
                onApprove={onApprove}
                approvingId={approvingId}
                onUnlockGeneration={onUnlockGeneration}
                unlockingId={unlockingId}
                onDeleteGeneration={onDeleteGeneration}
                deletingId={deletingId}
                onExportGeneration={onExportGeneration}
                exportingId={exportingId}
                bibleMatch={bibleMatch}
              />
            </ImageGallery>
          }
        />
        <Route
          path="documents"
          element={
            <DocumentsSection
              projectId={projectId}
              bibleVersion={project.bibleVersion}
              generations={generations}
              onApprove={onApprove}
              approvingId={approvingId}
              onUnlockGeneration={onUnlockGeneration}
              unlockingId={unlockingId}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
              onExportGeneration={onExportGeneration}
              exportingId={exportingId}
            />
          }
        />
        <Route
          path="animation"
          element={
            <AnimationStudio
              projectId={projectId}
              characters={structure?.characters}
              chapters={chapterUnits}
              generations={generations}
              onGenerated={upsertGeneration}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
              onCharacterUpdated={applyProjectMutation}
            />
          }
        />
        <Route
          path="library"
          element={
            <ClipLibrary
              projectId={projectId}
              characters={structure?.characters}
              generations={generations}
              onGenerated={upsertGeneration}
              onDeleteGeneration={onDeleteGeneration}
              deletingId={deletingId}
            />
          }
        />
        <Route
          path="settings"
          element={
            <SettingsSection
              project={project}
              bible={bible}
              onUpdated={refreshSection}
            />
          }
        />
        <Route path="*" element={<Navigate to={defaultSection} replace />} />
      </Routes>

    </div>
  );
};

// Retain legacy section implementations for CRA unused-vars.
void StructureSection;
void ChaptersSection;
void PagesSection;

export default ProjectPage;

